import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import { apiCall, reportAPI, recruiterAPI, paymentAPI, auditAPI, notificationAPI, paymentUploadAPI, studentAPI, userAPI, roleAPI, aiAPI } from '../services/api';
import { downloadRecruiterTemplate, downloadCSVTemplate, downloadFinancialReport, downloadRecruiterPaymentsCSV, downloadRecruiterPaymentHistory } from '../utils/csvUtils';
import { validateEmailForSubmission } from '../utils/validation';
import EmailInput from '../components/EmailInput';
import RolesPermissions from './RolesPermissions';
import { QRCodeSVG } from 'qrcode.react';
import Pagination from '../components/Pagination';
import ReportsSection from '../components/ReportsSection';
import bankBranchData from '../utils/bank_branch.json';
import LettersManagement from '../components/LettersManagement';
import SendReferralCodeEmails from './SendReferralCodeEmails';
import ExternalApiLogs from '../components/ExternalApiLogs';
import SettingsManagement from '../components/SettingsManagement';
import AdminAnalyticsDashboard from '../components/AdminAnalyticsDashboard';
import { PageHeader } from '../components/ui';
import Icon from '../components/Icon';

function AdminDashboard() {
  return (
    <Routes>
      <Route path="/" element={<AdminAnalyticsDashboard />} />
      <Route path="/recruiters" element={<Recruiters />} />
      <Route path="/recruiters/:id" element={<AdminRecruiterDetails />} />
      <Route path="/students" element={<AdminStudents />} />
      <Route path="/users" element={<UserManagement />} />
      <Route path="/payments" element={<PaymentsOverview />} />
      <Route path="/reports" element={<ReportsSection title="Reports" subtitle="Generate and export reports from live dashboard data" />} />
      <Route path="/payments/uploads" element={<PaymentUploads />} />
      <Route path="/payments/uploads/:id" element={<UploadDetails />} />
      <Route path="/payments/recruiters" element={<RecruiterPayments />} />
      <Route path="/payments/recruiters/:id" element={<RecruiterPaymentDetails />} />
      <Route path="/payments/reports" element={<PaymentReports />} />
      <Route path="/payments/analytics" element={<PaymentAnalytics />} />
      <Route path="/notifications" element={<AdminNotifications />} />
      <Route path="/audit" element={<AuditLogs />} />
      <Route path="/external-logs" element={<ExternalApiLogs />} />
      <Route path="/roles" element={<RolesPermissions />} />
      <Route path="/letters" element={<LettersManagement />} />
          <Route path="/referral-code-emails" element={<SendReferralCodeEmails />} />
          <Route path="/settings" element={<SettingsManagement />} />
    </Routes>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      const [statsResponse, notificationsResponse] = await Promise.all([
        reportAPI.getDashboard(),
        notificationAPI.getRecent(4, true) // true for admin notifications
      ]);
      setStats(statsResponse.data);
      setRecentNotifications(notificationsResponse.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  };

  // Enhanced data for charts - use real data from backend
  const performanceData = stats?.monthlyPerformance?.map(item => ({
    month: item.month_name,
    admissions: parseInt(item.admissions) || 0,
    enrollments: parseInt(item.enrollments) || 0,
    revenue: parseFloat(item.revenue) || 0
  })) || [
      { month: 'Jan', admissions: 0, enrollments: 0, revenue: 0 },
      { month: 'Feb', admissions: 0, enrollments: 0, revenue: 0 },
      { month: 'Mar', admissions: 0, enrollments: 0, revenue: 0 },
      { month: 'Apr', admissions: 0, enrollments: 0, revenue: 0 },
      { month: 'May', admissions: 0, enrollments: 0, revenue: 0 },
      { month: 'Jun', admissions: 0, enrollments: 0, revenue: 0 }
    ];

  const regionData = stats?.regionalData?.map((item, index) => ({
    name: item.region,
    value: parseInt(item.count) || 0,
    color: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#ef4444', '#6366f1'][index % 10]
  })) || [
      { name: 'No Data', value: 1, color: '#e2e8f0' }
    ];

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>

          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Dashboard...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching latest analytics and metrics</p>
        </div>
      </div>
    );
  }
  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin Dashboard</h1>
          <p className="page-subtitle">Comprehensive overview of recruiter performance and system analytics</p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => {
              // Generate and download dashboard report
              const reportData = `Admin Dashboard Report - ${new Date().toLocaleDateString()}\n\nSystem Overview:\n- Active Recruiters: ${stats?.overview?.activeRecruiters || 0}\n- Total Admissions: ${stats?.overview?.totalAdmissions || 0}\n- Total Enrollments: ${stats?.overview?.totalEnrollments || 0}\n- Total Commissions: KES ${Number(stats?.overview?.totalCommissions || 0).toFixed(2)}\n- Pending Payments: ${stats?.overview?.pendingPayments || 0}\n\nTop Recruiters:\n${stats?.topRecruiters?.map((r, i) => `${i + 1}. ${r.full_name} - KES ${Number(r.total_commission || 0).toFixed(2)}`).join('\n') || 'No data available'}\n\nGenerated: ${new Date().toLocaleString()}`;

              const blob = new Blob([reportData], { type: 'text/plain' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `dashboard_report_${new Date().toISOString().split('T')[0]}.txt`;
              link.click();
              URL.revokeObjectURL(url);

              toast.success('Dashboard report exported successfully!');
            }}
          >
            <Icon name="bar-chart" /> Export Report
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setLoading(true);
              loadDashboard();
              toast.success('Dashboard data refreshed!');
            }}
          >
            <Icon name="refresh" /> Refresh Data
          </button>
        </div>
      </div>

      {/* Enhanced Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Active Recruiters</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">{stats?.overview.activeRecruiters || 0}</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Admissions</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">{stats?.overview.totalAdmissions || 0}</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Enrollments</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">{stats?.overview.totalEnrollments || 0}</div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Revenue</div>
            <div className="stat-card-icon"></div>
          </div>
          <div className="stat-card-value">KES {Number(stats?.overview.totalCommissions || 0).toFixed(2)}</div>
        </div>


      </div>

      {/* Enhanced Dashboard Grid */}
      <div className="dashboard-grid">
        <div className="chart-container">
          <div className="card-header">
            <div>
              <h2 className="card-title">Performance Trends</h2>
              <p className="card-subtitle">Monthly admissions and enrollments (last 6 months)</p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <span style={{
                fontSize: '12px',
                backgroundColor: '#10b981',
                color: 'white',
                padding: '4px 8px',
                borderRadius: '12px',
                fontWeight: '600'
              }}>
                <Icon name="bar-chart" /> Active Rates
              </span>
              <Link to="/admin/payments" className="btn btn-outline btn-sm" style={{ textDecoration: 'none' }}>
                <Icon name="dollar" /> Manage Payments
              </Link>
            </div>
          </div>

          {/* Performance Chart Display */}
          <ResponsiveContainer width="100%" height={300}>
            {performanceData.length > 0 && performanceData.some(item => item.admissions > 0 || item.enrollments > 0) ? (
              <AreaChart data={performanceData}>
                <defs>
                  <linearGradient id="admissionsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#037b90" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#037b90" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="enrollmentsGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ff7f50" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#ff7f50" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" fontSize={12} fontWeight={600} />
                <YAxis stroke="#64748b" fontSize={12} fontWeight={600} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                    fontWeight: '600'
                  }}
                  formatter={(value, name) => {
                    if (name === 'Revenue') {
                      return [`KES ${Number(value).toFixed(2)}`, 'Revenue'];
                    }
                    return [value, name];
                  }}
                />
                <Area
                  type="monotone"
                  dataKey="admissions"
                  stroke="#3b82f6"
                  strokeWidth={3}
                  fill="url(#admissionsGradient)"
                  name="Admissions"
                />
                <Area
                  type="monotone"
                  dataKey="enrollments"
                  stroke="#10b981"
                  strokeWidth={3}
                  fill="url(#enrollmentsGradient)"
                  name="Enrollments"
                />
              </AreaChart>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                color: '#64748b'
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}><Icon name="bar-chart" /></div>
                <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Performance Data</h3>
                <p style={{ fontSize: '14px', textAlign: 'center' }}>
                  Performance trends will appear here once you have admissions and enrollments data
                </p>
              </div>
            )}
          </ResponsiveContainer>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Regional Distribution</h2>
                <p className="card-subtitle">Active recruiter distribution by county (live data)</p>
              </div>
              <span style={{
                fontSize: '10px',
                backgroundColor: '#3b82f6',
                color: 'white',
                padding: '2px 6px',
                borderRadius: '8px',
                fontWeight: '600'
              }}>
                <Icon name="map" /> Live
              </span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              {regionData.length > 0 && regionData[0].name !== 'No Data' ? (
                <PieChart>
                  <Pie
                    data={regionData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {regionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                      fontWeight: '600'
                    }}
                    formatter={(value, name) => [`${value} recruiters`, name]}
                  />
                </PieChart>
              ) : (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  color: '#64748b'
                }}>
                  <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}><Icon name="map" /></div>
                  <h4 style={{ fontWeight: '600', marginBottom: '4px', fontSize: '14px' }}>No Regional Data</h4>
                  <p style={{ fontSize: '12px', textAlign: 'center' }}>
                    Regional distribution will appear when recruiters are added
                  </p>
                </div>
              )}
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '16px' }}>
              {regionData.length > 0 && regionData[0].name !== 'No Data' ? (
                regionData.map((item, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}>
                    <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: item.color }}></div>
                    <span style={{ fontWeight: '600', color: '#64748b' }}>
                      {item.name} ({item.value})
                    </span>
                  </div>
                ))
              ) : (
                <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
                  Add recruiters to see regional distribution
                </div>
              )}
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <div>
                <h2 className="card-title">Quick Actions</h2>
                <p className="card-subtitle">Frequently used admin tasks</p>
              </div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <Link to="/admin/recruiters" className="btn btn-primary" style={{ justifyContent: 'flex-start', textDecoration: 'none' }}>
                <Icon name="user" /> Add New Recruiter
              </Link>
              <button
                className="btn btn-secondary"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => {
                  // Show options for bulk upload
                  const choice = window.confirm('Would you like to:\n\nOK - Download Template First\nCancel - Upload CSV File');

                  if (choice) {
                    // Download template
                    downloadRecruiterTemplate();
                    toast.success('Recruiter template downloaded! Fill it out and use bulk upload to import.');
                  } else {
                    // Create a file input element for bulk upload
                    const input = document.createElement('input');
                    input.type = 'file';
                    input.accept = '.csv';
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        toast.info(`Selected file: ${file.name}. Bulk upload functionality would process this CSV file.`);
                        // In a real implementation, this would handle the file upload
                      }
                    };
                    input.click();
                  }
                }}
              >
                <Icon name="upload" /> Bulk Upload
              </button>
              <Link to="/admin/payments" className="btn btn-outline" style={{ justifyContent: 'flex-start', textDecoration: 'none' }}>
                <Icon name="dollar" /> Process Payments
              </Link>
              <button
                className="btn btn-outline"
                style={{ justifyContent: 'flex-start' }}
                onClick={() => {
                  // Generate a quick report
                  toast.success('Generating monthly summary report...');
                  setTimeout(() => {
                    // Simulate report generation
                    const reportData = `Monthly Report - ${new Date().toLocaleDateString()}\n\nTotal Recruiters: ${stats?.overview?.activeRecruiters || 0}\nTotal Admissions: ${stats?.overview?.totalAdmissions || 0}\nTotal Commissions: KES ${Number(stats?.overview?.totalCommissions || 0).toFixed(2)}\n\nGenerated at: ${new Date().toLocaleString()}`;

                    const blob = new Blob([reportData], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `admin_report_${new Date().toISOString().split('T')[0]}.txt`;
                    link.click();
                    URL.revokeObjectURL(url);

                    toast.success('Monthly report generated and downloaded!');
                  }, 1500);
                }}
              >
                <Icon name="bar-chart" /> Generate Report
              </button>
              {/* <button 
                className="btn btn-outline" 
                style={{ justifyContent: 'flex-start' }}
                onClick={async () => {
                  const email = prompt('Enter email address to send test email:');
                  if (email && email.includes('@')) {
                    try {
                      toast.info('Sending test email...');
                      const token = localStorage.getItem('token');
                      const response = await fetch('http://localhost:5000/api/auth/test-email', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify({ email })
                      });
                      
                      const result = await response.json();
                      if (result.success) {
                        toast.success(`Test email sent successfully to ${email}!`);
                      } else {
                        toast.error('Failed to send test email: ' + result.error);
                      }
                    } catch (error) {
                      toast.error('Error sending test email: ' + error.message);
                    }
                  } else if (email !== null) {
                    toast.error('Please enter a valid email address');
                  }
                }}
              >
                Test Email
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Top Recruiters Table */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Top Performing Recruiters</h2>
            <p className="card-subtitle">Recruiters with highest commission earnings this month</p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                toast.info('Analytics feature - would show detailed recruiter performance charts and metrics');
              }}
            >
              <Icon name="trending-up" /> View Analytics
            </button>
            <Link to="/admin/recruiters" className="btn btn-primary btn-sm" style={{ textDecoration: 'none' }}>
              <Icon name="users" /> View All
            </Link>
          </div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Rank</th>
                <th>Recruiter</th>
                <th>Referral Code</th>
                <th>Admissions</th>
                <th>Enrollments</th>
                <th>Commission</th>
                {/* <th>Performance</th> */}
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {stats?.topRecruiters.map((recruiter, index) => (
                <tr key={recruiter.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{
                        fontSize: '20px',
                        filter: index === 0 ? 'hue-rotate(45deg)' : index === 1 ? 'hue-rotate(200deg)' : 'hue-rotate(30deg)'
                      }}>
                        {index === 0 ? <Icon name="medal-gold" /> : index === 1 ? <Icon name="medal-silver" /> : index === 2 ? <Icon name="medal-bronze" /> : <Icon name="medal" />}
                      </span>
                      <span style={{ fontWeight: '700', fontSize: '16px' }}>#{index + 1}</span>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${index % 2 === 0 ? '#3b82f6' : '#10b981'}, ${index % 2 === 0 ? '#8b5cf6' : '#f59e0b'})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '14px'
                      }}>
                        {recruiter.full_name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{recruiter.full_name}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="badge badge-info" style={{ fontSize: '12px', fontWeight: '700' }}>
                      {recruiter.referral_code}
                    </span>
                  </td>
                  <td>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{recruiter.admission_count}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: '18px', fontWeight: '700' }}>{recruiter.enrollment_count}</span>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                        KES {Number(recruiter.total_commission || 0).toFixed(2)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        Avg: KES {Number(recruiter.total_commission / (recruiter.admission_count + recruiter.enrollment_count) || 0).toFixed(2)}
                      </div>
                    </div>
                  </td>
                  {/* <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ 
                        width: '60px', 
                        height: '8px', 
                        backgroundColor: '#f1f5f9', 
                        borderRadius: '4px',
                        overflow: 'hidden'
                      }}>
                        <div style={{ 
                          width: `${Math.min((recruiter.enrollment_count / recruiter.admission_count) * 100, 100)}%`, 
                          height: '100%', 
                          background: 'linear-gradient(90deg, #10b981, #3b82f6)',
                          borderRadius: '4px'
                        }}></div>
                      </div>
                      <span style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                        {Math.round((recruiter.enrollment_count / recruiter.admission_count) * 100)}%
                      </span>
                    </div>
                  </td> */}
                  <td>
                    <span className="badge badge-success">Active</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Activity Timeline */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Recent Activity</h2>
            <p className="card-subtitle">Latest system activities and notifications</p>
          </div>
          <Link to="/admin/notifications" className="btn btn-outline btn-sm">View All</Link>
        </div>
        <div>
          {recentNotifications.length > 0 ? (
            recentNotifications.map((notification) => (
              <div key={notification.id} className="activity-item">
                <div className="activity-title">{notification.title}</div>
                <div className="activity-description">{notification.message}</div>
                <div className="activity-time">{formatTimeAgo(notification.created_at)}</div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}><Icon name="bell" /></div>
              <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Recent Activity</h3>
              <p style={{ fontSize: '14px' }}>System activities will appear here</p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
}
function Recruiters() {
  const navigate = useNavigate();
  const [recruiters, setRecruiters] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [nationalitySearch, setNationalitySearch] = useState('');
  const [showNationalityDropdown, setShowNationalityDropdown] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 10, page: 1 });

  // Edit mode state
  const [editingRecruiter, setEditingRecruiter] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  // Bulk upload state
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, status: '' });
  const [selectedFile, setSelectedFile] = useState(null);
  const [bulkJobId, setBulkJobId] = useState(null);
  const [bulkJobDone, setBulkJobDone] = useState(false);
  const [filePreview, setFilePreview] = useState(null);

  // Kenyan administrative data state
  const [countySearch, setCountySearch] = useState('');
  const [subcountySearch, setSubcountySearch] = useState('');
  const [showCountyDropdown, setShowCountyDropdown] = useState(false);
  const [showSubcountyDropdown, setShowSubcountyDropdown] = useState(false);

  // Phone number state
  const [phoneCountrySearch, setPhoneCountrySearch] = useState('');
  const [showPhoneCountryDropdown, setShowPhoneCountryDropdown] = useState(false);
  const [selectedPhoneCountry, setSelectedPhoneCountry] = useState({ code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254' });
  const [phoneNumber, setPhoneNumber] = useState('');

  // Email validation state
  const [emailValidation, setEmailValidation] = useState({ isValid: true, errors: [] });

  // Category state
  const [categories, setCategories] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showCategoryFilterDropdown, setShowCategoryFilterDropdown] = useState(false);
  // County filter state
  const [countyFilter, setCountyFilter] = useState('');
  const [countyFilterSearch, setCountyFilterSearch] = useState('');
  const [showCountyFilterDropdown, setShowCountyFilterDropdown] = useState(false);

  // Form data state - MUST be declared before filtering functions
  const [formData, setFormData] = useState({
    full_name: '',
    nationality: '',
    id_passport: '',
    kra_pin: '',
    county: '',
    subcounty: '',
    location: '',
    sublocation: '',
    village: '',
    mobile_number: '',
    email: '',
    residential_address: '',
    postal_address: '',
    bank_name: '',
    branch_code: '',
    bank_account: '',
    mobile_money_number: '',
    category_id: ''
  });
  const [submitting, setSubmitting] = useState(false);

  // Bank and Branch selection state
  const [bankSearch, setBankSearch] = useState('');
  const [branchSearch, setBranchSearch] = useState('');
  const [showBankDropdown, setShowBankDropdown] = useState(false);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);

  const selectedBank = bankBranchData.find(b => b.bank_name === formData.bank_name);
  const availableBranches = selectedBank ? selectedBank.branches : [];

  const filteredBanks = bankBranchData.filter(bank =>
    bank.bank_name.toLowerCase().includes(bankSearch.toLowerCase())
  );

  const filteredBranches = availableBranches.filter(branch =>
    branch.branch_name.toLowerCase().includes(branchSearch.toLowerCase()) ||
    branch.branch_code.includes(branchSearch)
  );

  const handleBankSelect = (bankName) => {
    setFormData({ ...formData, bank_name: bankName, branch_code: '' });
    setBankSearch(bankName);
    setShowBankDropdown(false);
    setBranchSearch('');
  };

  const handleBranchSelect = (branchCode, branchName) => {
    setFormData({ ...formData, branch_code: branchCode });
    setBranchSearch(`${branchCode} - ${branchName}`);
    setShowBranchDropdown(false);
  };

  // Comprehensive Kenyan Counties and Subcounties Data
  const kenyanCountiesData = [
    {
      "name": "Mombasa",
      "code": 1,
      "capital": "Mombasa City",
      "subCounties": ["Changamwe", "Jomvu", "Kisauni", "Likoni", "Mvita", "Nyali"]
    },
    {
      "name": "Kwale",
      "code": 2,
      "capital": "Kwale",
      "subCounties": ["Kinango", "Lungalunga", "Matuga", "Msambweni"]
    },
    {
      "name": "Kilifi",
      "code": 3,
      "capital": "Kilifi",
      "subCounties": ["Chonyi", "Ganze", "Kaloleni", "Kauma", "Kilifi North", "Kilifi South", "Magarini", "Malindi", "Rabai"]
    },
    {
      "name": "Tana River",
      "code": 4,
      "capital": "Hola",
      "subCounties": ["Tana North", "Tana Delta", "Tana River"]
    },
    {
      "name": "Lamu",
      "code": 5,
      "capital": "Lamu",
      "subCounties": ["Lamu East", "Lamu West"]
    },
    {
      "name": "Taita Taveta",
      "code": 6,
      "capital": "Voi",
      "subCounties": ["Mwatate", "Taita", "Taveta", "Voi"]
    },
    {
      "name": "Garissa",
      "code": 7,
      "capital": "Garissa",
      "subCounties": ["Balambala", "Dadaab", "Fafi", "Garissa", "Hulugho", "Ijara", "Lagdera"]
    },
    {
      "name": "Wajir",
      "code": 8,
      "capital": "Wajir",
      "subCounties": ["Buna", "Eldas", "Habaswein", "Tarbaj", "Wajir East", "Wajir North", "Wajir South", "Wajir West"]
    },
    {
      "name": "Mandera",
      "code": 9,
      "capital": "Mandera",
      "subCounties": ["Mandera West", "Banisa", "Kutulo", "Lafey", "Mandera Central", "Mandera East", "Mandera North"]
    },
    {
      "name": "Marsabit",
      "code": 10,
      "capital": "Marsabit",
      "subCounties": ["Loiyangalani", "Marsabit Central", "Marsabit North", "Marsabit South", "Moyale", "North Horr", "Sololo"]
    },
    {
      "name": "Isiolo",
      "code": 11,
      "capital": "Isiolo",
      "subCounties": ["Garbatulla", "Isiolo", "Merti"]
    },
    {
      "name": "Meru",
      "code": 12,
      "capital": "Meru",
      "subCounties": ["Buuri East", "Buuri West", "Igembe Central", "Igembe North", "Igembe South", "Imenti North", "Imenti South", "Meru Central", "Tigania Central", "Tigania East", "Tigania West"]
    },
    {
      "name": "Tharaka Nithi",
      "code": 13,
      "capital": "Kathwana",
      "subCounties": ["Igambang'ombe", "Maara", "Meru South", "Tharaka North", "Tharaka South"]
    },
    {
      "name": "Embu",
      "code": 14,
      "capital": "Embu",
      "subCounties": ["Embu East", "Embu North", "Embu West", "Mbeere South", "Mbeere North"]
    },
    {
      "name": "Kitui",
      "code": 15,
      "capital": "Kitui",
      "subCounties": ["Ikutha", "Katulani", "Kisasi", "Kitui Central", "Kitui West", "Kyuso", "Lower Yatta", "Matinyani", "Migwani", "Mumoni", "Mutitu", "Mutitu North", "Mutomo", "Mwingi Central", "Mwingi East", "Nzambani", "Thagicu", "Tseikuru"]
    },
    {
      "name": "Machakos",
      "code": 16,
      "capital": "Machakos",
      "subCounties": ["Athi River", "Kalama", "Kangundo", "Kathiani", "Machakos", "Masinga", "Matungulu", "Mwala", "Yatta"]
    },
    {
      "name": "Makueni",
      "code": 17,
      "capital": "Wote",
      "subCounties": ["Kathonzweni", "Kibwezi", "Kilungu", "Makindu", "Makueni", "Mbooni East", "Mbooni West", "Mukaa", "Nzaui"]
    },
    {
      "name": "Nyandarua",
      "code": 18,
      "capital": "Ol Kalou",
      "subCounties": ["Kinangop", "Nyandarua South", "Mirangine", "Kipipiri", "Nyandarua Central", "Nyandarua West", "Nyandarua North"]
    },
    {
      "name": "Nyeri",
      "code": 19,
      "capital": "Nyeri",
      "subCounties": ["Tetu", "Kieni East", "Kieni West", "Mathira East", "Mathira West", "Nyeri South", "Mukurwe ini", "Nyeri Central"]
    },
    {
      "name": "Kirinyaga",
      "code": 20,
      "capital": "Kerugoya/Kutus",
      "subCounties": ["Kirinyaga Central", "Kirinyaga East", "Kirinyaga West", "Mwea East", "Mwea West"]
    },
    {
      "name": "Murang'a",
      "code": 21,
      "capital": "Murang'a",
      "subCounties": ["Murang'a East", "Kangema", "Mathioya", "Kahuro", "Murang'a South", "Gatanga", "Kigumo", "Kandara"]
    },
    {
      "name": "Kiambu",
      "code": 22,
      "capital": "Kiambu",
      "subCounties": ["Gatundu North", "Gatundu South", "Githunguri", "Juja", "Kabete", "Kiambaa", "Kiambu", "Kikuyu", "Lari", "Limuru", "Ruiru", "Thika East", "Thika West"]
    },
    {
      "name": "Turkana",
      "code": 23,
      "capital": "Lodwar",
      "subCounties": ["Kibish", "Loima", "Turkana Central", "Turkana East", "Turkana North", "Turkana South", "Turkana West"]
    },
    {
      "name": "West Pokot",
      "code": 24,
      "capital": "Kapenguria",
      "subCounties": ["Kipkomo", "Pokot Central", "Pokot North", "Pokot South", "West Pokot"]
    },
    {
      "name": "Samburu",
      "code": 25,
      "capital": "Maralal",
      "subCounties": ["Samburu Central", "Samburu East", "Samburu North"]
    },
    {
      "name": "Trans Nzoia",
      "code": 26,
      "capital": "Kitale",
      "subCounties": ["Trans Nzoia West", "Trans Nzoia East", "Kwanza", "Endebess", "Kiminini"]
    },
    {
      "name": "Uasin Gishu",
      "code": 27,
      "capital": "Eldoret",
      "subCounties": ["Ainabkoi", "Kapseret", "Kesses", "Moiben", "Soy", "Turbo"]
    },
    {
      "name": "Elgeyo Marakwet",
      "code": 28,
      "capital": "Iten",
      "subCounties": ["Keiyo North", "Keiyo South", "Marakwet East", "Marakwet West"]
    },
    {
      "name": "Nandi",
      "code": 29,
      "capital": "Kapsabet",
      "subCounties": ["Chesumei", "Nandi Central", "Nandi East", "Nandi North", "Nandi South", "Tinderet"]
    },
    {
      "name": "Baringo",
      "code": 30,
      "capital": "Kabarnet",
      "subCounties": ["Baringo Central", "Baringo North", "East Pokot", "Koibatek", "Marigat", "Mogotio", "Tiaty East"]
    },
    {
      "name": "Laikipia",
      "code": 31,
      "capital": "Rumuruti",
      "subCounties": ["Laikipia Central", "Laikipia East", "Laikipia North", "Laikipia West", "Nyahururu"]
    },
    {
      "name": "Nakuru",
      "code": 32,
      "capital": "Nakuru",
      "subCounties": ["Gilgil", "Kuresoi North", "Kuresoi South", "Molo", "Naivasha", "Nakuru East", "Nakuru North", "Nakuru West", "Njoro", "Rongai", "Subukia"]
    },
    {
      "name": "Narok",
      "code": 33,
      "capital": "Narok",
      "subCounties": ["Narok East", "Narok North", "Narok South", "Narok West", "Trans Mara East", "Trans Mara West"]
    },
    {
      "name": "Kajiado",
      "code": 34,
      "capital": "Kajiado",
      "subCounties": ["Isinya", "Kajiado Central", "Kajiado North", "Kajiado West", "Loitokitok", "Mashuuru"]
    },
    {
      "name": "Kericho",
      "code": 35,
      "capital": "Kericho",
      "subCounties": ["Belgut", "Bureti", "Kericho East", "Kipkelion", "Londiani", "Soin Sigowet"]
    },
    {
      "name": "Bomet",
      "code": 36,
      "capital": "Bomet",
      "subCounties": ["Bomet East", "Chepalungu", "Konoin", "Sotik", "Bomet Central"]
    },
    {
      "name": "Kakamega",
      "code": 37,
      "capital": "Kakamega",
      "subCounties": ["Butere", "Kakamega Central", "Kakamega East", "Kakamega North", "Kakamega South", "Khwisero", "Likuyani", "Lugari", "Matete", "Matungu", "Mumias East", "Mumias West", "Navakholo"]
    },
    {
      "name": "Vihiga",
      "code": 38,
      "capital": "Vihiga",
      "subCounties": ["Emuhaya", "Vihiga", "Sabatia", "Luanda", "Hamisi"]
    },
    {
      "name": "Bungoma",
      "code": 39,
      "capital": "Bungoma",
      "subCounties": ["Bumula", "Bungoma Central", "Bungoma East", "Bungoma North", "Bungoma South", "Cheptais", "Kimilili Bungoma", "Mt Elgon", "Bungoma West", "Tongaren", "Webuye West"]
    },
    {
      "name": "Busia",
      "code": 40,
      "capital": "Busia",
      "subCounties": ["Bunyala", "Busia", "Butula", "Nambale", "Samia", "Teso North", "Teso South"]
    },
    {
      "name": "Siaya",
      "code": 41,
      "capital": "Siaya",
      "subCounties": ["Siaya", "Gem", "Ugenya", "Ugunja", "Bondo", "Rarieda"]
    },
    {
      "name": "Kisumu",
      "code": 42,
      "capital": "Kisumu",
      "subCounties": ["Kisumu East", "Kisumu Central", "Kisumu West", "Seme", "Muhoroni", "Nyando", "Nyakach"]
    },
    {
      "name": "Homa Bay",
      "code": 43,
      "capital": "Homa Bay",
      "subCounties": ["Homa Bay", "Ndhiwa", "Rachuonyo North", "Rachuonyo East", "Rachuonyo South", "Rangwe", "Suba North", "Suba South"]
    },
    {
      "name": "Migori",
      "code": 44,
      "capital": "Migori",
      "subCounties": ["Awendo", "Kuria East", "Kuria West", "Nyatike", "Rongo", "Suna East", "Suna West", "Uriri"]
    },
    {
      "name": "Kisii",
      "code": 45,
      "capital": "Kisii",
      "subCounties": ["Etago", "Gucha", "Gucha South", "Kenyenya", "Kisii Central", "Kisii South", "Kitutu Central", "Marani", "Masaba South", "Nyamache", "Sameta"]
    },
    {
      "name": "Nyamira",
      "code": 46,
      "capital": "Nyamira",
      "subCounties": ["Borabu", "Manga", "Masaba North", "Nyamira North", "Nyamira South"]
    },
    {
      "name": "Nairobi",
      "code": 47,
      "capital": "Nairobi City",
      "subCounties": ["Dagoretti", "Embakasi", "Kamukunji", "Kasarani", "Kibra", "Lang'ata", "Makadara", "Mathare", "Njiru", "Starehe", "Westlands"]
    }
  ];

  // Countries data with flags
  const countries = [
    { code: 'AF', name: 'Afghan', flag: '🇦🇫' },
    { code: 'AL', name: 'Albanian', flag: '🇦🇱' },
    { code: 'DZ', name: 'Algerian', flag: '🇩🇿' },
    { code: 'US', name: 'American', flag: '🇺🇸' },
    { code: 'AD', name: 'Andorran', flag: '🇦🇩' },
    { code: 'AO', name: 'Angolan', flag: '🇦🇴' },
    { code: 'AR', name: 'Argentine', flag: '🇦🇷' },
    { code: 'AM', name: 'Armenian', flag: '🇦🇲' },
    { code: 'AU', name: 'Australian', flag: '🇦🇺' },
    { code: 'AT', name: 'Austrian', flag: '🇦🇹' },
    { code: 'AZ', name: 'Azerbaijani', flag: '🇦🇿' },
    { code: 'BS', name: 'Bahamian', flag: '🇧🇸' },
    { code: 'BH', name: 'Bahraini', flag: '🇧🇭' },
    { code: 'BD', name: 'Bangladeshi', flag: '🇧🇩' },
    { code: 'BB', name: 'Barbadian', flag: '🇧🇧' },
    { code: 'BY', name: 'Belarusian', flag: '🇧🇾' },
    { code: 'BE', name: 'Belgian', flag: '🇧🇪' },
    { code: 'BZ', name: 'Belizean', flag: '🇧🇿' },
    { code: 'BJ', name: 'Beninese', flag: '🇧🇯' },
    { code: 'BT', name: 'Bhutanese', flag: '🇧🇹' },
    { code: 'BO', name: 'Bolivian', flag: '🇧🇴' },
    { code: 'BA', name: 'Bosnian', flag: '🇧🇦' },
    { code: 'BW', name: 'Botswanan', flag: '🇧🇼' },
    { code: 'BR', name: 'Brazilian', flag: '🇧🇷' },
    { code: 'GB', name: 'British', flag: '🇬🇧' },
    { code: 'BN', name: 'Bruneian', flag: '🇧🇳' },
    { code: 'BG', name: 'Bulgarian', flag: '🇧🇬' },
    { code: 'BF', name: 'Burkinabe', flag: '🇧🇫' },
    { code: 'BI', name: 'Burundian', flag: '🇧🇮' },
    { code: 'KH', name: 'Cambodian', flag: '🇰🇭' },
    { code: 'CM', name: 'Cameroonian', flag: '🇨🇲' },
    { code: 'CA', name: 'Canadian', flag: '🇨🇦' },
    { code: 'CV', name: 'Cape Verdean', flag: '🇨🇻' },
    { code: 'CF', name: 'Central African', flag: '🇨🇫' },
    { code: 'TD', name: 'Chadian', flag: '🇹🇩' },
    { code: 'CL', name: 'Chilean', flag: '🇨🇱' },
    { code: 'CN', name: 'Chinese', flag: '🇨🇳' },
    { code: 'CO', name: 'Colombian', flag: '🇨🇴' },
    { code: 'KM', name: 'Comoran', flag: '🇰🇲' },
    { code: 'CG', name: 'Congolese', flag: '🇨🇬' },
    { code: 'CR', name: 'Costa Rican', flag: '🇨🇷' },
    { code: 'HR', name: 'Croatian', flag: '🇭🇷' },
    { code: 'CU', name: 'Cuban', flag: '🇨🇺' },
    { code: 'CY', name: 'Cypriot', flag: '🇨🇾' },
    { code: 'CZ', name: 'Czech', flag: '🇨🇿' },
    { code: 'DK', name: 'Danish', flag: '🇩🇰' },
    { code: 'DJ', name: 'Djiboutian', flag: '🇩🇯' },
    { code: 'DO', name: 'Dominican', flag: '🇩🇴' },
    { code: 'NL', name: 'Dutch', flag: '🇳🇱' },
    { code: 'EC', name: 'Ecuadorian', flag: '🇪🇨' },
    { code: 'EG', name: 'Egyptian', flag: '🇪🇬' },
    { code: 'SV', name: 'Salvadoran', flag: '🇸🇻' },
    { code: 'GQ', name: 'Equatorial Guinean', flag: '🇬🇶' },
    { code: 'ER', name: 'Eritrean', flag: '🇪🇷' },
    { code: 'EE', name: 'Estonian', flag: '🇪🇪' },
    { code: 'SZ', name: 'Eswatini', flag: '🇸🇿' },
    { code: 'ET', name: 'Ethiopian', flag: '🇪🇹' },
    { code: 'FJ', name: 'Fijian', flag: '🇫🇯' },
    { code: 'FI', name: 'Finnish', flag: '🇫🇮' },
    { code: 'FR', name: 'French', flag: '🇫🇷' },
    { code: 'GA', name: 'Gabonese', flag: '🇬🇦' },
    { code: 'GM', name: 'Gambian', flag: '🇬🇲' },
    { code: 'GE', name: 'Georgian', flag: '🇬🇪' },
    { code: 'DE', name: 'German', flag: '🇩🇪' },
    { code: 'GH', name: 'Ghanaian', flag: '🇬🇭' },
    { code: 'GR', name: 'Greek', flag: '🇬🇷' },
    { code: 'GD', name: 'Grenadian', flag: '🇬🇩' },
    { code: 'GT', name: 'Guatemalan', flag: '🇬🇹' },
    { code: 'GN', name: 'Guinean', flag: '🇬🇳' },
    { code: 'GW', name: 'Guinea-Bissauan', flag: '🇬🇼' },
    { code: 'GY', name: 'Guyanese', flag: '🇬🇾' },
    { code: 'HT', name: 'Haitian', flag: '🇭🇹' },
    { code: 'HN', name: 'Honduran', flag: '🇭🇳' },
    { code: 'HU', name: 'Hungarian', flag: '🇭🇺' },
    { code: 'IS', name: 'Icelandic', flag: '🇮🇸' },
    { code: 'IN', name: 'Indian', flag: '🇮🇳' },
    { code: 'ID', name: 'Indonesian', flag: '🇮🇩' },
    { code: 'IR', name: 'Iranian', flag: '🇮🇷' },
    { code: 'IQ', name: 'Iraqi', flag: '🇮🇶' },
    { code: 'IE', name: 'Irish', flag: '🇮🇪' },
    { code: 'IL', name: 'Israeli', flag: '🇮🇱' },
    { code: 'IT', name: 'Italian', flag: '🇮🇹' },
    { code: 'CI', name: 'Ivorian', flag: '🇨🇮' },
    { code: 'JM', name: 'Jamaican', flag: '🇯🇲' },
    { code: 'JP', name: 'Japanese', flag: '🇯🇵' },
    { code: 'JO', name: 'Jordanian', flag: '🇯🇴' },
    { code: 'KZ', name: 'Kazakhstani', flag: '🇰🇿' },
    { code: 'KE', name: 'Kenyan', flag: '🇰🇪' },
    { code: 'KI', name: 'Kiribati', flag: '🇰🇮' },
    { code: 'KP', name: 'North Korean', flag: '🇰🇵' },
    { code: 'KR', name: 'South Korean', flag: '🇰🇷' },
    { code: 'KW', name: 'Kuwaiti', flag: '🇰🇼' },
    { code: 'KG', name: 'Kyrgyzstani', flag: '🇰🇬' },
    { code: 'LA', name: 'Laotian', flag: '🇱🇦' },
    { code: 'LV', name: 'Latvian', flag: '🇱🇻' },
    { code: 'LB', name: 'Lebanese', flag: '🇱🇧' },
    { code: 'LS', name: 'Lesotho', flag: '🇱🇸' },
    { code: 'LR', name: 'Liberian', flag: '🇱🇷' },
    { code: 'LY', name: 'Libyan', flag: '🇱🇾' },
    { code: 'LI', name: 'Liechtensteiner', flag: '🇱🇮' },
    { code: 'LT', name: 'Lithuanian', flag: '🇱🇹' },
    { code: 'LU', name: 'Luxembourgish', flag: '🇱🇺' },
    { code: 'MK', name: 'Macedonian', flag: '🇲🇰' },
    { code: 'MG', name: 'Malagasy', flag: '🇲🇬' },
    { code: 'MW', name: 'Malawian', flag: '🇲🇼' },
    { code: 'MY', name: 'Malaysian', flag: '🇲🇾' },
    { code: 'MV', name: 'Maldivian', flag: '🇲🇻' },
    { code: 'ML', name: 'Malian', flag: '🇲🇱' },
    { code: 'MT', name: 'Maltese', flag: '🇲🇹' },
    { code: 'MH', name: 'Marshallese', flag: '🇲🇭' },
    { code: 'MR', name: 'Mauritanian', flag: '🇲🇷' },
    { code: 'MU', name: 'Mauritian', flag: '🇲🇺' },
    { code: 'MX', name: 'Mexican', flag: '🇲🇽' },
    { code: 'FM', name: 'Micronesian', flag: '🇫🇲' },
    { code: 'MD', name: 'Moldovan', flag: '🇲🇩' },
    { code: 'MC', name: 'Monacan', flag: '🇲🇨' },
    { code: 'MN', name: 'Mongolian', flag: '🇲🇳' },
    { code: 'ME', name: 'Montenegrin', flag: '🇲🇪' },
    { code: 'MA', name: 'Moroccan', flag: '🇲🇦' },
    { code: 'MZ', name: 'Mozambican', flag: '🇲🇿' },
    { code: 'MM', name: 'Burmese', flag: '🇲🇲' },
    { code: 'NA', name: 'Namibian', flag: '🇳🇦' },
    { code: 'NR', name: 'Nauruan', flag: '🇳🇷' },
    { code: 'NP', name: 'Nepalese', flag: '🇳🇵' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿' },
    { code: 'NI', name: 'Nicaraguan', flag: '🇳🇮' },
    { code: 'NE', name: 'Nigerien', flag: '🇳🇪' },
    { code: 'NG', name: 'Nigerian', flag: '🇳🇬' },
    { code: 'NO', name: 'Norwegian', flag: '🇳🇴' },
    { code: 'OM', name: 'Omani', flag: '🇴🇲' },
    { code: 'PK', name: 'Pakistani', flag: '🇵🇰' },
    { code: 'PW', name: 'Palauan', flag: '🇵🇼' },
    { code: 'PS', name: 'Palestinian', flag: '🇵🇸' },
    { code: 'PA', name: 'Panamanian', flag: '🇵🇦' },
    { code: 'PG', name: 'Papua New Guinean', flag: '🇵🇬' },
    { code: 'PY', name: 'Paraguayan', flag: '🇵🇾' },
    { code: 'PE', name: 'Peruvian', flag: '🇵🇪' },
    { code: 'PH', name: 'Filipino', flag: '🇵🇭' },
    { code: 'PL', name: 'Polish', flag: '🇵🇱' },
    { code: 'PT', name: 'Portuguese', flag: '🇵🇹' },
    { code: 'QA', name: 'Qatari', flag: '🇶🇦' },
    { code: 'RO', name: 'Romanian', flag: '🇷🇴' },
    { code: 'RU', name: 'Russian', flag: '🇷🇺' },
    { code: 'RW', name: 'Rwandan', flag: '🇷🇼' },
    { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳' },
    { code: 'LC', name: 'Saint Lucian', flag: '🇱🇨' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨' },
    { code: 'WS', name: 'Samoan', flag: '🇼🇸' },
    { code: 'SM', name: 'San Marinese', flag: '🇸🇲' },
    { code: 'ST', name: 'Sao Tomean', flag: '🇸🇹' },
    { code: 'SA', name: 'Saudi Arabian', flag: '🇸🇦' },
    { code: 'SN', name: 'Senegalese', flag: '🇸🇳' },
    { code: 'RS', name: 'Serbian', flag: '🇷🇸' },
    { code: 'SC', name: 'Seychellois', flag: '🇸🇨' },
    { code: 'SL', name: 'Sierra Leonean', flag: '🇸🇱' },
    { code: 'SG', name: 'Singaporean', flag: '🇸🇬' },
    { code: 'SK', name: 'Slovak', flag: '🇸🇰' },
    { code: 'SI', name: 'Slovenian', flag: '🇸🇮' },
    { code: 'SB', name: 'Solomon Islander', flag: '🇸🇧' },
    { code: 'SO', name: 'Somali', flag: '🇸🇴' },
    { code: 'ZA', name: 'South African', flag: '🇿🇦' },
    { code: 'SS', name: 'South Sudanese', flag: '🇸🇸' },
    { code: 'ES', name: 'Spanish', flag: '🇪🇸' },
    { code: 'LK', name: 'Sri Lankan', flag: '🇱🇰' },
    { code: 'SD', name: 'Sudanese', flag: '🇸🇩' },
    { code: 'SR', name: 'Surinamese', flag: '🇸🇷' },
    { code: 'SE', name: 'Swedish', flag: '🇸🇪' },
    { code: 'CH', name: 'Swiss', flag: '🇨🇭' },
    { code: 'SY', name: 'Syrian', flag: '🇸🇾' },
    { code: 'TW', name: 'Taiwanese', flag: '🇹🇼' },
    { code: 'TJ', name: 'Tajikistani', flag: '🇹🇯' },
    { code: 'TZ', name: 'Tanzanian', flag: '🇹🇿' },
    { code: 'TH', name: 'Thai', flag: '🇹🇭' },
    { code: 'TL', name: 'Timorese', flag: '🇹🇱' },
    { code: 'TG', name: 'Togolese', flag: '🇹🇬' },
    { code: 'TO', name: 'Tongan', flag: '🇹🇴' },
    { code: 'TT', name: 'Trinidadian', flag: '🇹🇹' },
    { code: 'TN', name: 'Tunisian', flag: '🇹🇳' },
    { code: 'TR', name: 'Turkish', flag: '🇹🇷' },
    { code: 'TM', name: 'Turkmen', flag: '🇹🇲' },
    { code: 'TV', name: 'Tuvaluan', flag: '🇹🇻' },
    { code: 'UG', name: 'Ugandan', flag: '🇺🇬' },
    { code: 'UA', name: 'Ukrainian', flag: '🇺🇦' },
    { code: 'AE', name: 'Emirati', flag: '🇦🇪' },
    { code: 'UY', name: 'Uruguayan', flag: '🇺🇾' },
    { code: 'UZ', name: 'Uzbekistani', flag: '🇺🇿' },
    { code: 'VU', name: 'Vanuatuan', flag: '🇻🇺' },
    { code: 'VA', name: 'Vatican', flag: '🇻🇦' },
    { code: 'VE', name: 'Venezuelan', flag: '🇻🇪' },
    { code: 'VN', name: 'Vietnamese', flag: '🇻🇳' },
    { code: 'YE', name: 'Yemeni', flag: '🇾🇪' },
    { code: 'ZM', name: 'Zambian', flag: '🇿🇲' },
    { code: 'ZW', name: 'Zimbabwean', flag: '🇿🇼' }
  ];

  // Countries data with flags and dial codes for phone numbers
  const phoneCountriesData = [
    { code: 'AF', name: 'Afghanistan', flag: '🇦🇫', dialCode: '+93' },
    { code: 'AL', name: 'Albania', flag: '🇦🇱', dialCode: '+355' },
    { code: 'DZ', name: 'Algeria', flag: '🇩🇿', dialCode: '+213' },
    { code: 'AS', name: 'American Samoa', flag: '🇦🇸', dialCode: '+1684' },
    { code: 'AD', name: 'Andorra', flag: '🇦🇩', dialCode: '+376' },
    { code: 'AO', name: 'Angola', flag: '🇦🇴', dialCode: '+244' },
    { code: 'AI', name: 'Anguilla', flag: '🇦🇮', dialCode: '+1264' },
    { code: 'AG', name: 'Antigua and Barbuda', flag: '🇦🇬', dialCode: '+1268' },
    { code: 'AR', name: 'Argentina', flag: '🇦🇷', dialCode: '+54' },
    { code: 'AM', name: 'Armenia', flag: '🇦🇲', dialCode: '+374' },
    { code: 'AW', name: 'Aruba', flag: '🇦🇼', dialCode: '+297' },
    { code: 'AU', name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
    { code: 'AT', name: 'Austria', flag: '🇦🇹', dialCode: '+43' },
    { code: 'AZ', name: 'Azerbaijan', flag: '🇦🇿', dialCode: '+994' },
    { code: 'BS', name: 'Bahamas', flag: '🇧🇸', dialCode: '+1242' },
    { code: 'BH', name: 'Bahrain', flag: '🇧🇭', dialCode: '+973' },
    { code: 'BD', name: 'Bangladesh', flag: '🇧🇩', dialCode: '+880' },
    { code: 'BB', name: 'Barbados', flag: '🇧🇧', dialCode: '+1246' },
    { code: 'BY', name: 'Belarus', flag: '🇧🇾', dialCode: '+375' },
    { code: 'BE', name: 'Belgium', flag: '🇧🇪', dialCode: '+32' },
    { code: 'BZ', name: 'Belize', flag: '🇧🇿', dialCode: '+501' },
    { code: 'BJ', name: 'Benin', flag: '🇧🇯', dialCode: '+229' },
    { code: 'BM', name: 'Bermuda', flag: '🇧🇲', dialCode: '+1441' },
    { code: 'BT', name: 'Bhutan', flag: '🇧🇹', dialCode: '+975' },
    { code: 'BO', name: 'Bolivia', flag: '🇧🇴', dialCode: '+591' },
    { code: 'BA', name: 'Bosnia and Herzegovina', flag: '🇧🇦', dialCode: '+387' },
    { code: 'BW', name: 'Botswana', flag: '🇧🇼', dialCode: '+267' },
    { code: 'BR', name: 'Brazil', flag: '🇧🇷', dialCode: '+55' },
    { code: 'BN', name: 'Brunei', flag: '🇧🇳', dialCode: '+673' },
    { code: 'BG', name: 'Bulgaria', flag: '🇧🇬', dialCode: '+359' },
    { code: 'BF', name: 'Burkina Faso', flag: '🇧🇫', dialCode: '+226' },
    { code: 'BI', name: 'Burundi', flag: '🇧🇮', dialCode: '+257' },
    { code: 'KH', name: 'Cambodia', flag: '🇰🇭', dialCode: '+855' },
    { code: 'CM', name: 'Cameroon', flag: '🇨🇲', dialCode: '+237' },
    { code: 'CA', name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
    { code: 'CV', name: 'Cape Verde', flag: '🇨🇻', dialCode: '+238' },
    { code: 'KY', name: 'Cayman Islands', flag: '🇰🇾', dialCode: '+1345' },
    { code: 'CF', name: 'Central African Republic', flag: '🇨🇫', dialCode: '+236' },
    { code: 'TD', name: 'Chad', flag: '🇹🇩', dialCode: '+235' },
    { code: 'CL', name: 'Chile', flag: '🇨🇱', dialCode: '+56' },
    { code: 'CN', name: 'China', flag: '🇨🇳', dialCode: '+86' },
    { code: 'CO', name: 'Colombia', flag: '🇨🇴', dialCode: '+57' },
    { code: 'KM', name: 'Comoros', flag: '🇰🇲', dialCode: '+269' },
    { code: 'CG', name: 'Congo', flag: '🇨🇬', dialCode: '+242' },
    { code: 'CD', name: 'Congo (DRC)', flag: '🇨🇩', dialCode: '+243' },
    { code: 'CK', name: 'Cook Islands', flag: '🇨🇰', dialCode: '+682' },
    { code: 'CR', name: 'Costa Rica', flag: '🇨🇷', dialCode: '+506' },
    { code: 'CI', name: 'Côte d\'Ivoire', flag: '🇨🇮', dialCode: '+225' },
    { code: 'HR', name: 'Croatia', flag: '🇭🇷', dialCode: '+385' },
    { code: 'CU', name: 'Cuba', flag: '🇨🇺', dialCode: '+53' },
    { code: 'CY', name: 'Cyprus', flag: '🇨🇾', dialCode: '+357' },
    { code: 'CZ', name: 'Czech Republic', flag: '🇨🇿', dialCode: '+420' },
    { code: 'DK', name: 'Denmark', flag: '🇩🇰', dialCode: '+45' },
    { code: 'DJ', name: 'Djibouti', flag: '🇩🇯', dialCode: '+253' },
    { code: 'DM', name: 'Dominica', flag: '🇩🇲', dialCode: '+1767' },
    { code: 'DO', name: 'Dominican Republic', flag: '🇩🇴', dialCode: '+1809' },
    { code: 'EC', name: 'Ecuador', flag: '🇪🇨', dialCode: '+593' },
    { code: 'EG', name: 'Egypt', flag: '🇪🇬', dialCode: '+20' },
    { code: 'SV', name: 'El Salvador', flag: '🇸🇻', dialCode: '+503' },
    { code: 'GQ', name: 'Equatorial Guinea', flag: '🇬🇶', dialCode: '+240' },
    { code: 'ER', name: 'Eritrea', flag: '🇪🇷', dialCode: '+291' },
    { code: 'EE', name: 'Estonia', flag: '🇪🇪', dialCode: '+372' },
    { code: 'ET', name: 'Ethiopia', flag: '🇪🇹', dialCode: '+251' },
    { code: 'FJ', name: 'Fiji', flag: '🇫🇯', dialCode: '+679' },
    { code: 'FI', name: 'Finland', flag: '🇫🇮', dialCode: '+358' },
    { code: 'FR', name: 'France', flag: '🇫🇷', dialCode: '+33' },
    { code: 'GA', name: 'Gabon', flag: '🇬🇦', dialCode: '+241' },
    { code: 'GM', name: 'Gambia', flag: '🇬🇲', dialCode: '+220' },
    { code: 'GE', name: 'Georgia', flag: '🇬🇪', dialCode: '+995' },
    { code: 'DE', name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
    { code: 'GH', name: 'Ghana', flag: '🇬🇭', dialCode: '+233' },
    { code: 'GR', name: 'Greece', flag: '🇬🇷', dialCode: '+30' },
    { code: 'GD', name: 'Grenada', flag: '🇬🇩', dialCode: '+1473' },
    { code: 'GT', name: 'Guatemala', flag: '🇬🇹', dialCode: '+502' },
    { code: 'GN', name: 'Guinea', flag: '🇬🇳', dialCode: '+224' },
    { code: 'GW', name: 'Guinea-Bissau', flag: '🇬🇼', dialCode: '+245' },
    { code: 'GY', name: 'Guyana', flag: '🇬🇾', dialCode: '+592' },
    { code: 'HT', name: 'Haiti', flag: '🇭🇹', dialCode: '+509' },
    { code: 'HN', name: 'Honduras', flag: '🇭🇳', dialCode: '+504' },
    { code: 'HK', name: 'Hong Kong', flag: '🇭🇰', dialCode: '+852' },
    { code: 'HU', name: 'Hungary', flag: '🇭🇺', dialCode: '+36' },
    { code: 'IS', name: 'Iceland', flag: '🇮🇸', dialCode: '+354' },
    { code: 'IN', name: 'India', flag: '🇮🇳', dialCode: '+91' },
    { code: 'ID', name: 'Indonesia', flag: '🇮🇩', dialCode: '+62' },
    { code: 'IR', name: 'Iran', flag: '🇮🇷', dialCode: '+98' },
    { code: 'IQ', name: 'Iraq', flag: '🇮🇶', dialCode: '+964' },
    { code: 'IE', name: 'Ireland', flag: '🇮🇪', dialCode: '+353' },
    { code: 'IL', name: 'Israel', flag: '🇮🇱', dialCode: '+972' },
    { code: 'IT', name: 'Italy', flag: '🇮🇹', dialCode: '+39' },
    { code: 'JM', name: 'Jamaica', flag: '🇯🇲', dialCode: '+1876' },
    { code: 'JP', name: 'Japan', flag: '🇯🇵', dialCode: '+81' },
    { code: 'JO', name: 'Jordan', flag: '🇯🇴', dialCode: '+962' },
    { code: 'KZ', name: 'Kazakhstan', flag: '🇰🇿', dialCode: '+7' },
    { code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254' },
    { code: 'KI', name: 'Kiribati', flag: '🇰🇮', dialCode: '+686' },
    { code: 'KP', name: 'North Korea', flag: '🇰🇵', dialCode: '+850' },
    { code: 'KR', name: 'South Korea', flag: '🇰🇷', dialCode: '+82' },
    { code: 'KW', name: 'Kuwait', flag: '🇰🇼', dialCode: '+965' },
    { code: 'KG', name: 'Kyrgyzstan', flag: '🇰🇬', dialCode: '+996' },
    { code: 'LA', name: 'Laos', flag: '🇱🇦', dialCode: '+856' },
    { code: 'LV', name: 'Latvia', flag: '🇱🇻', dialCode: '+371' },
    { code: 'LB', name: 'Lebanon', flag: '🇱🇧', dialCode: '+961' },
    { code: 'LS', name: 'Lesotho', flag: '🇱🇸', dialCode: '+266' },
    { code: 'LR', name: 'Liberia', flag: '🇱🇷', dialCode: '+231' },
    { code: 'LY', name: 'Libya', flag: '🇱🇾', dialCode: '+218' },
    { code: 'LI', name: 'Liechtenstein', flag: '🇱🇮', dialCode: '+423' },
    { code: 'LT', name: 'Lithuania', flag: '🇱🇹', dialCode: '+370' },
    { code: 'LU', name: 'Luxembourg', flag: '🇱🇺', dialCode: '+352' },
    { code: 'MO', name: 'Macao', flag: '🇲🇴', dialCode: '+853' },
    { code: 'MK', name: 'Macedonia', flag: '🇲🇰', dialCode: '+389' },
    { code: 'MG', name: 'Madagascar', flag: '🇲🇬', dialCode: '+261' },
    { code: 'MW', name: 'Malawi', flag: '🇲🇼', dialCode: '+265' },
    { code: 'MY', name: 'Malaysia', flag: '🇲🇾', dialCode: '+60' },
    { code: 'MV', name: 'Maldives', flag: '🇲🇻', dialCode: '+960' },
    { code: 'ML', name: 'Mali', flag: '🇲🇱', dialCode: '+223' },
    { code: 'MT', name: 'Malta', flag: '🇲🇹', dialCode: '+356' },
    { code: 'MH', name: 'Marshall Islands', flag: '🇲🇭', dialCode: '+692' },
    { code: 'MR', name: 'Mauritania', flag: '🇲🇷', dialCode: '+222' },
    { code: 'MU', name: 'Mauritius', flag: '🇲🇺', dialCode: '+230' },
    { code: 'MX', name: 'Mexico', flag: '🇲🇽', dialCode: '+52' },
    { code: 'FM', name: 'Micronesia', flag: '🇫🇲', dialCode: '+691' },
    { code: 'MD', name: 'Moldova', flag: '🇲🇩', dialCode: '+373' },
    { code: 'MC', name: 'Monaco', flag: '🇲🇨', dialCode: '+377' },
    { code: 'MN', name: 'Mongolia', flag: '🇲🇳', dialCode: '+976' },
    { code: 'ME', name: 'Montenegro', flag: '🇲🇪', dialCode: '+382' },
    { code: 'MA', name: 'Morocco', flag: '🇲🇦', dialCode: '+212' },
    { code: 'MZ', name: 'Mozambique', flag: '🇲🇿', dialCode: '+258' },
    { code: 'MM', name: 'Myanmar', flag: '🇲🇲', dialCode: '+95' },
    { code: 'NA', name: 'Namibia', flag: '🇳🇦', dialCode: '+264' },
    { code: 'NR', name: 'Nauru', flag: '🇳🇷', dialCode: '+674' },
    { code: 'NP', name: 'Nepal', flag: '🇳🇵', dialCode: '+977' },
    { code: 'NL', name: 'Netherlands', flag: '🇳🇱', dialCode: '+31' },
    { code: 'NZ', name: 'New Zealand', flag: '🇳🇿', dialCode: '+64' },
    { code: 'NI', name: 'Nicaragua', flag: '🇳🇮', dialCode: '+505' },
    { code: 'NE', name: 'Niger', flag: '🇳🇪', dialCode: '+227' },
    { code: 'NG', name: 'Nigeria', flag: '🇳🇬', dialCode: '+234' },
    { code: 'NO', name: 'Norway', flag: '🇳🇴', dialCode: '+47' },
    { code: 'OM', name: 'Oman', flag: '🇴🇲', dialCode: '+968' },
    { code: 'PK', name: 'Pakistan', flag: '🇵🇰', dialCode: '+92' },
    { code: 'PW', name: 'Palau', flag: '🇵🇼', dialCode: '+680' },
    { code: 'PS', name: 'Palestine', flag: '🇵🇸', dialCode: '+970' },
    { code: 'PA', name: 'Panama', flag: '🇵🇦', dialCode: '+507' },
    { code: 'PG', name: 'Papua New Guinea', flag: '🇵🇬', dialCode: '+675' },
    { code: 'PY', name: 'Paraguay', flag: '🇵🇾', dialCode: '+595' },
    { code: 'PE', name: 'Peru', flag: '🇵🇪', dialCode: '+51' },
    { code: 'PH', name: 'Philippines', flag: '🇵🇭', dialCode: '+63' },
    { code: 'PL', name: 'Poland', flag: '🇵🇱', dialCode: '+48' },
    { code: 'PT', name: 'Portugal', flag: '🇵🇹', dialCode: '+351' },
    { code: 'PR', name: 'Puerto Rico', flag: '🇵🇷', dialCode: '+1787' },
    { code: 'QA', name: 'Qatar', flag: '🇶🇦', dialCode: '+974' },
    { code: 'RO', name: 'Romania', flag: '🇷🇴', dialCode: '+40' },
    { code: 'RU', name: 'Russia', flag: '🇷🇺', dialCode: '+7' },
    { code: 'RW', name: 'Rwanda', flag: '🇷🇼', dialCode: '+250' },
    { code: 'KN', name: 'Saint Kitts and Nevis', flag: '🇰🇳', dialCode: '+1869' },
    { code: 'LC', name: 'Saint Lucia', flag: '🇱🇨', dialCode: '+1758' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines', flag: '🇻🇨', dialCode: '+1784' },
    { code: 'WS', name: 'Samoa', flag: '🇼🇸', dialCode: '+685' },
    { code: 'SM', name: 'San Marino', flag: '🇸🇲', dialCode: '+378' },
    { code: 'ST', name: 'São Tomé and Príncipe', flag: '🇸🇹', dialCode: '+239' },
    { code: 'SA', name: 'Saudi Arabia', flag: '🇸🇦', dialCode: '+966' },
    { code: 'SN', name: 'Senegal', flag: '🇸🇳', dialCode: '+221' },
    { code: 'RS', name: 'Serbia', flag: '🇷🇸', dialCode: '+381' },
    { code: 'SC', name: 'Seychelles', flag: '🇸🇨', dialCode: '+248' },
    { code: 'SL', name: 'Sierra Leone', flag: '🇸🇱', dialCode: '+232' },
    { code: 'SG', name: 'Singapore', flag: '🇸🇬', dialCode: '+65' },
    { code: 'SK', name: 'Slovakia', flag: '🇸🇰', dialCode: '+421' },
    { code: 'SI', name: 'Slovenia', flag: '🇸🇮', dialCode: '+386' },
    { code: 'SB', name: 'Solomon Islands', flag: '🇸🇧', dialCode: '+677' },
    { code: 'SO', name: 'Somalia', flag: '🇸🇴', dialCode: '+252' },
    { code: 'ZA', name: 'South Africa', flag: '🇿🇦', dialCode: '+27' },
    { code: 'SS', name: 'South Sudan', flag: '🇸🇸', dialCode: '+211' },
    { code: 'ES', name: 'Spain', flag: '🇪🇸', dialCode: '+34' },
    { code: 'LK', name: 'Sri Lanka', flag: '🇱🇰', dialCode: '+94' },
    { code: 'SD', name: 'Sudan', flag: '🇸🇩', dialCode: '+249' },
    { code: 'SR', name: 'Suriname', flag: '🇸🇷', dialCode: '+597' },
    { code: 'SZ', name: 'Swaziland', flag: '🇸🇿', dialCode: '+268' },
    { code: 'SE', name: 'Sweden', flag: '🇸🇪', dialCode: '+46' },
    { code: 'CH', name: 'Switzerland', flag: '🇨🇭', dialCode: '+41' },
    { code: 'SY', name: 'Syria', flag: '🇸🇾', dialCode: '+963' },
    { code: 'TW', name: 'Taiwan', flag: '🇹🇼', dialCode: '+886' },
    { code: 'TJ', name: 'Tajikistan', flag: '🇹🇯', dialCode: '+992' },
    { code: 'TZ', name: 'Tanzania', flag: '🇹🇿', dialCode: '+255' },
    { code: 'TH', name: 'Thailand', flag: '🇹🇭', dialCode: '+66' },
    { code: 'TL', name: 'Timor-Leste', flag: '🇹🇱', dialCode: '+670' },
    { code: 'TG', name: 'Togo', flag: '🇹🇬', dialCode: '+228' },
    { code: 'TO', name: 'Tonga', flag: '🇹🇴', dialCode: '+676' },
    { code: 'TT', name: 'Trinidad and Tobago', flag: '🇹🇹', dialCode: '+1868' },
    { code: 'TN', name: 'Tunisia', flag: '🇹🇳', dialCode: '+216' },
    { code: 'TR', name: 'Turkey', flag: '🇹🇷', dialCode: '+90' },
    { code: 'TM', name: 'Turkmenistan', flag: '🇹🇲', dialCode: '+993' },
    { code: 'TV', name: 'Tuvalu', flag: '🇹🇻', dialCode: '+688' },
    { code: 'UG', name: 'Uganda', flag: '🇺🇬', dialCode: '+256' },
    { code: 'UA', name: 'Ukraine', flag: '🇺🇦', dialCode: '+380' },
    { code: 'AE', name: 'United Arab Emirates', flag: '🇦🇪', dialCode: '+971' },
    { code: 'GB', name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
    { code: 'US', name: 'United States', flag: '🇺🇸', dialCode: '+1' },
    { code: 'UY', name: 'Uruguay', flag: '🇺🇾', dialCode: '+598' },
    { code: 'UZ', name: 'Uzbekistan', flag: '🇺🇿', dialCode: '+998' },
    { code: 'VU', name: 'Vanuatu', flag: '🇻🇺', dialCode: '+678' },
    { code: 'VA', name: 'Vatican City', flag: '🇻🇦', dialCode: '+39' },
    { code: 'VE', name: 'Venezuela', flag: '🇻🇪', dialCode: '+58' },
    { code: 'VN', name: 'Vietnam', flag: '🇻🇳', dialCode: '+84' },
    { code: 'YE', name: 'Yemen', flag: '🇾🇪', dialCode: '+967' },
    { code: 'ZM', name: 'Zambia', flag: '🇿🇲', dialCode: '+260' },
    { code: 'ZW', name: 'Zimbabwe', flag: '🇿🇼', dialCode: '+263' }
  ];

  // Filter countries based on search
  const filteredCountries = countries.filter(country =>
    country.name.toLowerCase().includes(nationalitySearch.toLowerCase())
  );

  // Filter phone countries based on search
  const filteredPhoneCountries = phoneCountriesData.filter(country =>
    country.name.toLowerCase().includes(phoneCountrySearch.toLowerCase()) ||
    country.dialCode.includes(phoneCountrySearch)
  );

  // Filter Kenyan counties based on search
  const filteredCounties = kenyanCountiesData.filter(county =>
    county.name.toLowerCase().includes(countySearch.toLowerCase())
  );

  // Get subcounties for selected county
  const getSubcountiesForCounty = (countyName) => {
    const county = kenyanCountiesData.find(c => c.name === countyName);
    return county ? county.subCounties : [];
  };

  // Filter subcounties based on search and selected county
  const filteredSubcounties = getSubcountiesForCounty(formData.county).filter(subcounty =>
    subcounty.toLowerCase().includes(subcountySearch.toLowerCase())
  );

  const handleNationalitySelect = (nationality) => {
    setFormData(prev => ({
      ...prev,
      nationality: nationality
    }));
    setNationalitySearch(nationality);
    setShowNationalityDropdown(false);
  };

  // Kenyan administrative handlers
  const handleCountySelect = (countyName) => {
    setFormData(prev => ({
      ...prev,
      county: countyName,
      subcounty: '' // Reset subcounty when county changes
    }));
    setCountySearch(countyName);
    setSubcountySearch('');
    setShowCountyDropdown(false);
  };

  const handleSubcountySelect = (subcountyName) => {
    setFormData(prev => ({
      ...prev,
      subcounty: subcountyName
    }));
    setSubcountySearch(subcountyName);
    setShowSubcountyDropdown(false);
  };

  // Phone country handler
  const handlePhoneCountrySelect = (country) => {
    setSelectedPhoneCountry(country);
    setPhoneCountrySearch(country.name);
    setShowPhoneCountryDropdown(false);
    // Update the mobile_number field with the new country code
    const currentNumber = phoneNumber.replace(/^\+\d+\s*/, ''); // Remove existing country code
    const newFullNumber = `${country.dialCode} ${currentNumber}`.trim();
    setFormData(prev => ({
      ...prev,
      mobile_number: newFullNumber
    }));
  };

  // Phone number handler
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value;
    setPhoneNumber(value);
    const fullNumber = `${selectedPhoneCountry.dialCode} ${value}`.trim();
    setFormData(prev => ({
      ...prev,
      mobile_number: fullNumber
    }));
  };

  // Email validation handler
  const handleEmailValidationChange = (validation) => {
    setEmailValidation(validation);
  };

  // Email change handler
  const handleEmailChange = (value) => {
    setFormData(prev => ({
      ...prev,
      email: value
    }));
  };

  // Category filter handler
  const handleCategoryFilterSelect = (categoryName) => {
    setCategoryFilter(categoryName);
    setShowCategoryFilterDropdown(false);
  };

  // County filter handler
  const handleCountyFilterSelect = (countyName) => {
    setCountyFilter(countyName);
    setCountyFilterSearch(countyName);
    setShowCountyFilterDropdown(false);
  };

  useEffect(() => {
    apiCall('/recruiters/categories').then(res => setCategories(res.data)).catch(() => {});
  }, []);

  // Debounced search — fires API 300ms after the user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      loadRecruiters();
    }, 300);
    return () => clearTimeout(handler);
  }, [currentPage, searchTerm, countyFilter, categoryFilter]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, countyFilter, categoryFilter]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.nationality-dropdown')) {
        setShowNationalityDropdown(false);
      }
      if (!event.target.closest('.county-dropdown')) {
        setShowCountyDropdown(false);
      }
      if (!event.target.closest('.subcounty-dropdown')) {
        setShowSubcountyDropdown(false);
      }
      if (!event.target.closest('.phone-country-dropdown')) {
        setShowPhoneCountryDropdown(false);
      }
      if (!event.target.closest('.county-filter-dropdown')) {
        setShowCountyFilterDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [currentPage]);

  const loadRecruiters = async () => {
    try {
      const response = await recruiterAPI.getAll(currentPage, pagination.limit, searchTerm, countyFilter, categoryFilter);
      setRecruiters(response.data.recruiters || []);
      setPagination(response.data.pagination || { total: 0, limit: 10, page: 1 });
    } catch (error) {
      toast.error('Failed to load recruiters');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    // Validate email before submission
    const emailValidationResult = validateEmailForSubmission(formData.email, 'Email Address');
    if (!emailValidationResult.isValid) {
      toast.error(emailValidationResult.errors.join(', '));
      setSubmitting(false);
      return;
    }

    // Check if email validation component shows errors
    if (!emailValidation.isValid) {
      toast.error('Please fix email validation errors before submitting');
      setSubmitting(false);
      return;
    }

    try {
      if (isEditMode && editingRecruiter) {
        // Update existing recruiter
        await recruiterAPI.update(editingRecruiter.id, formData);
        toast.success(" Recruiter updated successfully!");
      } else {
        // Create new recruiter
        await recruiterAPI.create(formData);
        toast.success('Recruiter created successfully! Login credentials have been sent via email.');
      }

      // Reset form and state
      setShowForm(false);
      setIsEditMode(false);
      setEditingRecruiter(null);
      setFormData({
        full_name: '',
        nationality: '',
        id_passport: '',
        kra_pin: '',
        county: '',
        subcounty: '',
        location: '',
        sublocation: '',
        village: '',
        mobile_number: '',
        email: '',
        residential_address: '',
        postal_address: '',
        bank_account: '',
        mobile_money_number: '',
        category_id: ''
      });
      setNationalitySearch('');
      setCountySearch('');
      setSubcountySearch('');
      setPhoneCountrySearch('');
      setPhoneNumber('');
      setSelectedPhoneCountry({ code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254' });
      setEmailValidation({ isValid: true, errors: [] });
      loadRecruiters();
    } catch (error) {
      const action = isEditMode ? 'update' : 'create';
      toast.error(`Failed to ${action} recruiter: ` + (error.response?.data?.error || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['.csv', '.xlsx', '.xls'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));
    if (!validTypes.includes(fileExtension)) {
      toast.error(" Invalid file type. Please upload CSV or Excel files only.");
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(" File too large. Maximum size is 10MB.");
      return;
    }

    setSelectedFile(file);

    // Create file preview
    if (file.name.endsWith('.csv')) {
      const text = await file.text();
      const lines = text.split('\n').slice(0, 10); // Show first 10 lines
      const preview = lines.filter(line => !line.startsWith('#')).slice(0, 5); // Show first 5 data lines
      setFilePreview({
        type: 'csv',
        lines: preview,
        totalLines: text.split('\n').length,
        headers: preview[0]?.split(',') || []
      });
    } else {
      setFilePreview({
        type: 'excel',
        name: file.name,
        size: (file.size / 1024).toFixed(2) + ' KB'
      });
    }

    toast.success(` File "${file.name}" selected successfully. Review and click Submit to process.`);
  };

  const handleBulkSubmit = async () => {
    if (!selectedFile) {
      toast.error(" Please select a file first.");
      return;
    }

    setBulkUploadLoading(true);
    setBulkJobId(null);
    setBulkJobDone(false);
    setUploadProgress({ current: 0, total: 0, status: 'Uploading and validating file...' });

    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const response = await recruiterAPI.bulkUpload(formData);
      const { jobId, summary, errors = [], message } = response.data || {};

      console.log('Bulk upload response:', response.data);

      const inserted = summary?.inserted || 0;
      const totalInFile = summary?.totalInFile || 0;
      const validationErrors = summary?.validationErrors || 0;
      const insertErrors = summary?.insertErrors || 0;
      const totalFailed = validationErrors + insertErrors;

      // Set initial progress — emails start sending in background
      setUploadProgress({
        current: 0,
        total: inserted,
        status: inserted > 0 ? `${inserted} recruiters added. Sending emails in background...` : 'Processing complete'
      });

      if (inserted > 0) {
        toast.success(` ${inserted} recruiters inserted successfully!`);
        if (totalFailed > 0) {
          toast.warning(` ${totalFailed} rows had errors (see below).`);
        }
        // Begin background job tracking
        setBulkJobId(jobId);
        setSelectedFile(null);
        setFilePreview(null);
        await loadRecruiters();
      } else {
        toast.error(` No recruiters were added. ${totalFailed} row(s) had errors.`);
        if (errors.length > 0) {
          const topErrors = errors.slice(0, 3).map(e => `Row ${e.row}: ${e.error}`).join(' | ');
          setTimeout(() => toast.error(` Errors: ${topErrors}`), 1500);
        }
      }

      // Log errors to console for reference
      if (errors.length > 0) {
        console.group(" Bulk Upload Row Errors");
        console.table(errors);
        console.groupEnd();
      }

    } catch (error) {
      console.error('Bulk upload error:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Unknown error occurred';
      toast.error(" Bulk upload failed: " + errorMessage);
      setUploadProgress({ current: 0, total: 0, status: 'Upload failed' });

      if (errorMessage.includes('No valid recruiter data')) {
        setTimeout(() => toast.info(" Tip: Check your CSV headers match the template exactly."), 2000);
      } else if (errorMessage.includes('Authentication required') || errorMessage.includes('Invalid token')) {
        setTimeout(() => toast.info(" Tip: Your session may have expired. Please refresh the page and login again."), 2000);
      }
    } finally {
      setBulkUploadLoading(false);
    }
  };

  // Poll background email job status every 4 seconds
  useEffect(() => {
    if (!bulkJobId || bulkJobDone) return;

    const intervalId = setInterval(async () => {
      try {
        const statusRes = await recruiterAPI.getBulkUploadStatus(bulkJobId);
        const job = statusRes.data;

        setUploadProgress({
          current: job.emails_sent || 0,
          total: job.inserted || 0,
          status: job.status === 'done'
            ? ` Done! ${job.emails_sent} emails sent, ${job.emails_failed} failed.`
            : ` Sending emails... ${job.emails_sent} / ${job.inserted}`
        });

        if (job.status === 'done' || job.status === 'failed') {
          setBulkJobDone(true);
          clearInterval(intervalId);
          if (job.status === 'done') {
            toast.success(` All emails sent! ${job.emails_sent} delivered, ${job.emails_failed} failed.`);
          }
        }
      } catch (_) {
        // ignore polling errors silently
      }
    }, 4000);

    return () => clearInterval(intervalId);
  }, [bulkJobId, bulkJobDone]);

  const handleClearFile = () => {
    setSelectedFile(null);
    setFilePreview(null);
    toast.info(" File selection cleared.");
  };

  const handleDelete = async (id) => {
    if (!window.confirm(" Are you sure you want to delete this recruiter? This action cannot be undone.")) return;

    try {
      await recruiterAPI.delete(id);
      toast.success(" Recruiter deleted successfully");
      loadRecruiters();
    } catch (error) {
      toast.error(" Failed to delete recruiter");
    }
  };

  const handleEdit = (recruiter) => {
    setEditingRecruiter(recruiter);
    setIsEditMode(true);
    setShowForm(true);

    // Populate form with existing data
    setFormData({
      full_name: recruiter.full_name || '',
      nationality: recruiter.nationality || '',
      id_passport: recruiter.id_passport || '',
      kra_pin: recruiter.kra_pin || '',
      county: recruiter.county || '',
      subcounty: recruiter.subcounty || '',
      location: recruiter.location || '',
      sublocation: recruiter.sublocation || '',
      village: recruiter.village || '',
      mobile_number: recruiter.mobile_number || '',
      email: recruiter.email || '',
      residential_address: recruiter.residential_address || '',
      postal_address: recruiter.postal_address || '',
      bank_name: recruiter.bank_name || '',
      branch_code: recruiter.branch_code || '',
      bank_account: recruiter.bank_account || '',
      mobile_money_number: recruiter.mobile_money_number || '',
      category_id: recruiter.category_id || ''
    });

    // Set search fields for bank dropdowns
    setBankSearch(recruiter.bank_name || '');
    setBranchSearch(recruiter.branch_code || '');

    // Set search fields for dropdowns
    setNationalitySearch(recruiter.nationality || '');
    setCountySearch(recruiter.county || '');
    setSubcountySearch(recruiter.subcounty || '');

    // Set phone country based on mobile number
    if (recruiter.mobile_number) {
      const phoneCountry = phoneCountriesData.find(country =>
        recruiter.mobile_number.startsWith(country.dialCode)
      );
      if (phoneCountry) {
        setSelectedPhoneCountry(phoneCountry);
        setPhoneCountrySearch(phoneCountry.name);
        setPhoneNumber(recruiter.mobile_number.replace(phoneCountry.dialCode, '').trim());
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setIsEditMode(false);
    setEditingRecruiter(null);

    // Reset form data
    setFormData({
      full_name: '',
      nationality: '',
      id_passport: '',
      kra_pin: '',
      county: '',
      subcounty: '',
      location: '',
      sublocation: '',
      village: '',
      mobile_number: '',
      email: '',
      residential_address: '',
      postal_address: '',
      bank_account: '',
      mobile_money_number: ''
    });

    // Reset search fields
    setNationalitySearch('');
    setCountySearch('');
    setSubcountySearch('');
    setPhoneCountrySearch('');
    setPhoneNumber('');
    setSelectedPhoneCountry({ code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254' });
    setEmailValidation({ isValid: true, errors: [] });
  };

  const handleAddRecruiter = () => {
    setIsEditMode(false);
    setEditingRecruiter(null);
    setShowForm(!showForm);

    // Reset form data
    setFormData({
      full_name: '',
      nationality: '',
      id_passport: '',
      kra_pin: '',
      county: '',
      subcounty: '',
      location: '',
      sublocation: '',
      village: '',
      mobile_number: '',
      email: '',
      residential_address: '',
      postal_address: '',
      bank_account: '',
      mobile_money_number: ''
    });

    // Reset search fields
    setNationalitySearch('');
    setCountySearch('');
    setSubcountySearch('');
    setPhoneCountrySearch('');
    setPhoneNumber('');
    setSelectedPhoneCountry({ code: 'KE', name: 'Kenya', flag: '🇰🇪', dialCode: '+254' });
    setEmailValidation({ isValid: true, errors: [] });
  };
  const filteredCountiesForFilter = kenyanCountiesData.filter(county =>
    county.name.toLowerCase().includes(countyFilterSearch.toLowerCase())
  );

  const displayRecruiters = recruiters;

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="users" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Recruiters...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching recruiter data and statistics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <PageHeader
        title="Recruiters Management"
        subtitle="Manage recruiter accounts, track performance, and handle registrations"
        icon="users"
        actions={
          <>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                downloadRecruiterTemplate();
                toast.success('Recruiter template downloaded successfully!');
              }}
            >
              <Icon name="download" /> Download Template
            </button>
          <button
            type="button"
            className="btn btn-outline btn-sm"
            style={{ margin: 0, height: '32px', display: 'inline-flex', alignItems: 'center' }}
            onClick={() => document.getElementById('bulk-upload-input').click()}
            disabled={bulkUploadLoading}
          >
            <Icon name="upload" /> Bulk Upload
          </button>
          <input
            id="bulk-upload-input"
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            disabled={bulkUploadLoading}
          />
            <button
              className="btn btn-primary btn-sm"
              onClick={handleAddRecruiter}
            >
              Add Recruiter
            </button>
          </>
      }
      />

      {/* Bulk Upload Instructions */}
      <div className="card" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            backgroundColor: '#3b82f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '18px'
          }}>
            <Icon name="lightbulb" />
          </div>
          <div style={{ flex: 1 }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#1f2937' }}>
              Bulk Upload Instructions
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: 0 }}>
              1. Click "<Icon name="download" /> Download Template" to get the CSV format •
              2. Fill in recruiter information •
              3. Save as CSV •
              4. Use "<Icon name="upload" /> Bulk Upload" to choose your file •
              5. Review the preview and click "<Icon name="rocket" /> Submit & Process"
            </p>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <span style={{
              fontSize: '12px',
              padding: '4px 8px',
              backgroundColor: '#10b981',
              color: 'white',
              borderRadius: '4px',
              fontWeight: '600'
            }}>
              CSV Format
            </span>
            <span style={{
              fontSize: '12px',
              padding: '4px 8px',
              backgroundColor: '#f59e0b',
              color: 'white',
              borderRadius: '4px',
              fontWeight: '600'
            }}>
              15 Fields
            </span>
          </div>
        </div>
      </div>

      {/* Bulk Upload Progress */}
      {(bulkUploadLoading || uploadProgress.status) && (
        <div className="card" style={{ backgroundColor: bulkJobDone ? '#f0fdf4' : '#f0f9ff', border: `1px solid ${bulkJobDone ? '#22c55e' : '#0ea5e9'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '50%',
              backgroundColor: bulkJobDone ? '#22c55e' : bulkUploadLoading ? '#3b82f6' : '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '20px',
              flexShrink: 0
            }}>
              {bulkJobDone ? <Icon name="check-circle" /> : bulkUploadLoading ? <Icon name="clock" /> : <Icon name="mail" />}
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '4px', color: '#1f2937' }}>
                {bulkJobDone ? 'All Done!' : bulkUploadLoading ? 'Uploading & Inserting Records...' : 'Sending Welcome Emails in Background'}
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 8px' }}>
                {uploadProgress.status}
              </p>
              {uploadProgress.total > 0 && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                    <span><Icon name="mail" /> Emails sent: {uploadProgress.current} / {uploadProgress.total}</span>
                    <span>{Math.round((uploadProgress.current / uploadProgress.total) * 100)}%</span>
                  </div>
                  <div style={{ width: '100%', height: '10px', backgroundColor: '#e5e7eb', borderRadius: '5px', overflow: 'hidden' }}>
                    <div style={{
                      width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                      height: '100%',
                      background: bulkJobDone
                        ? 'linear-gradient(90deg, #22c55e, #16a34a)'
                        : 'linear-gradient(90deg, #3b82f6, #0ea5e9)',
                      borderRadius: '5px',
                      transition: 'width 0.5s ease'
                    }} />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}



      {/* File Preview and Submit */}
      {selectedFile && (
        <div className="card" style={{ backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              backgroundColor: '#10b981',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontSize: '18px'
            }}>
              <Icon name="folder" />
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '8px', color: '#1f2937' }}>
                File Selected: {selectedFile.name}
              </h3>
              <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '16px' }}>
                Size: {(selectedFile.size / 1024).toFixed(2)} KB •
                Type: {selectedFile.name.split('.').pop().toUpperCase()}
              </p>

              {/* File Preview */}
              {filePreview && (
                <div style={{
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '16px'
                }}>
                  <h4 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#374151' }}>
                    File Preview:
                  </h4>
                  {filePreview.type === 'csv' ? (
                    <div>
                      <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '8px' }}>
                        Showing first 5 data rows (Total: {filePreview.totalLines} lines)
                      </p>
                      <div style={{
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        backgroundColor: '#f9fafb',
                        padding: '8px',
                        borderRadius: '4px',
                        overflow: 'auto',
                        maxHeight: '150px'
                      }}>
                        {filePreview.lines.map((line, index) => (
                          <div key={index} style={{
                            marginBottom: '2px',
                            color: index === 0 ? '#059669' : '#374151',
                            fontWeight: index === 0 ? '600' : '400'
                          }}>
                            {line}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p style={{ fontSize: '12px', color: '#6b7280' }}>
                      Excel file ready for processing. Preview not available for Excel files.
                    </p>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  className={`btn btn-primary btn-sm ${bulkUploadLoading ? 'loading' : ''}`}
                  onClick={handleBulkSubmit}
                  disabled={bulkUploadLoading}
                >
                  {bulkUploadLoading ? <><Icon name="clock" />{" Processing..."}</> : <><Icon name="rocket" />{" Submit & Process"}</>}
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleClearFile}
                  disabled={bulkUploadLoading}
                >
                  <Icon name="trash" /> Clear File
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="card">
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search recruiters by name, email, or referral code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {/* County Filter Dropdown */}
            <div style={{ position: 'relative', minWidth: '200px' }} className="county-filter-dropdown">
              <input
                type="text"
                className="form-select"
                placeholder="All Counties"
                value={countyFilterSearch}
                onChange={(e) => {
                  setCountyFilterSearch(e.target.value);
                  setShowCountyFilterDropdown(true);
                  // Clear filter if search is empty
                  if (!e.target.value.trim()) {
                    setCountyFilter('');
                  }
                }}
                onFocus={() => setShowCountyFilterDropdown(true)}
                style={{
                  minWidth: '200px',
                  cursor: 'text',
                  backgroundColor: 'white'
                }}
              />
              {showCountyFilterDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  backgroundColor: 'white',
                  border: '1px solid #e2e8f0',
                  borderRadius: '8px',
                  boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                  maxHeight: '200px',
                  overflowY: 'auto',
                  zIndex: 1000
                }}>
                  {/* Clear Filter Option */}
                  <div
                    onClick={() => {
                      setCountyFilter('');
                      setCountyFilterSearch('');
                      setShowCountyFilterDropdown(false);
                    }}
                    style={{
                      padding: '12px 16px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #f1f5f9',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#6b7280',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    <span style={{ fontSize: '16px' }}><Icon name="refresh" /></span>
                    <span>All Counties</span>
                  </div>

                  {filteredCountiesForFilter.length > 0 ? (
                    filteredCountiesForFilter.map((county) => (
                      <div
                        key={county.code}
                        onClick={() => handleCountyFilterSelect(county.name)}
                        style={{
                          padding: '12px 16px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f1f5f9',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          fontSize: '14px',
                          transition: 'background-color 0.2s',
                          backgroundColor: countyFilter === county.name ? '#f0f9ff' : 'white'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = countyFilter === county.name ? '#f0f9ff' : 'white'}
                      >
                        <span style={{ fontSize: '16px' }}><Icon name="landmark" /></span>
                        <div>
                          <div style={{ fontWeight: '600' }}>{county.name}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>Capital: {county.capital}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div style={{
                      padding: '12px 16px',
                      color: '#64748b',
                      fontSize: '14px',
                      textAlign: 'center'
                    }}>
                      No counties found
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Category Filter Dropdown */}
            <div style={{ position: 'relative', minWidth: '160px' }}>
              <div
                className="form-select"
                onClick={() => setShowCategoryFilterDropdown(!showCategoryFilterDropdown)}
                style={{ cursor: 'pointer', color: categoryFilter ? '#1e293b' : '#94a3b8' }}
              >
                <span>{categoryFilter ? (categories.find(c => c.name === categoryFilter)?.display_name || categoryFilter) : <><Icon name="folder" />{" All Categories"}</>}</span>
                <svg style={{ width: '14px', height: '14px', transition: 'transform 0.2s', transform: showCategoryFilterDropdown ? 'rotate(180deg)' : 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/></svg>
              </div>
              {showCategoryFilterDropdown && (
                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, marginTop: '4px', background: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                  <div
                    onClick={() => { handleCategoryFilterSelect(''); setShowCategoryFilterDropdown(false); }}
                    style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: !categoryFilter ? '#f0f9ff' : 'transparent', color: '#64748b', borderBottom: '1px solid #f1f5f9' }}
                  >
                    All Categories
                  </div>
                  {categories.map((cat) => (
                    <div
                      key={cat.id}
                      onClick={() => { handleCategoryFilterSelect(cat.name); setShowCategoryFilterDropdown(false); }}
                      style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '14px', background: categoryFilter === cat.name ? '#f0f9ff' : 'transparent', borderBottom: '1px solid #f1f5f9' }}
                      onMouseEnter={(e) => e.target.style.background = categoryFilter === cat.name ? '#f0f9ff' : '#f8fafc'}
                      onMouseLeave={(e) => e.target.style.background = categoryFilter === cat.name ? '#f0f9ff' : 'transparent'}
                    >
                      {cat.display_name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <select className="form-select" style={{ minWidth: '150px' }}>
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Active Filters Display */}
      {(countyFilter || searchTerm || categoryFilter) && (
        <div className="card" style={{ padding: '12px 20px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#64748b' }}>Active Filters:</span>

            {searchTerm && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 12px',
                backgroundColor: '#dbeafe',
                borderRadius: '20px',
                fontSize: '13px',
                color: '#1e40af'
              }}>
                <span><Icon name="search" /> Search: "{searchTerm}"</span>
                <button
                  onClick={() => setSearchTerm('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#1e40af',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '0 4px'
                  }}
                >
                  <Icon name="close" />
                </button>
              </div>
            )}

            {countyFilter && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 12px',
                backgroundColor: '#dcfce7',
                borderRadius: '20px',
                fontSize: '13px',
                color: '#166534'
              }}>
                <span><Icon name="landmark" /> County: {countyFilter}</span>
                <button
                  onClick={() => {
                    setCountyFilter('');
                    setCountyFilterSearch('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#166534',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '0 4px'
                  }}
                >
                  <Icon name="close" />
                </button>
              </div>
            )}

            {categoryFilter && (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '4px 12px',
                backgroundColor: '#fef3c7',
                borderRadius: '20px',
                fontSize: '13px',
                color: '#92400e'
              }}>
                <span><Icon name="folder" /> Category: {categories.find(c => c.name === categoryFilter)?.display_name || categoryFilter}</span>
                <button
                  onClick={() => setCategoryFilter('')}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#92400e',
                    cursor: 'pointer',
                    fontSize: '12px',
                    padding: '0 4px'
                  }}
                >
                  <Icon name="close" />
                </button>
              </div>
            )}

            <button
              onClick={() => {
                setSearchTerm('');
                setCountyFilter('');
                setCountyFilterSearch('');
                setCategoryFilter('');
              }}
              style={{
                background: 'none',
                border: '1px solid #d1d5db',
                color: '#6b7280',
                cursor: 'pointer',
                fontSize: '12px',
                padding: '4px 8px',
                borderRadius: '4px',
                marginLeft: 'auto'
              }}
            >
              Clear All
            </button>
          </div>
        </div>
      )}

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">{isEditMode ? 'Edit Recruiter' : 'Add New Recruiter'}</h3>
            <p className="card-subtitle">
              {isEditMode
                ? 'Update recruiter information and biodata'
                : 'Complete biodata collection for new recruiter registration'
              }
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            {/* Personal Information Section */}
            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '16px', borderBottom: '2px solid #037b90', paddingBottom: '8px' }}>
                Personal Information
              </h4>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    name="full_name"
                    className="form-input"
                    placeholder="Enter full legal name"
                    value={formData.full_name}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nationality *</label>
                  <div style={{ position: 'relative' }} className="nationality-dropdown">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search nationality..."
                      value={nationalitySearch}
                      onChange={(e) => {
                        setNationalitySearch(e.target.value);
                        setShowNationalityDropdown(true);
                      }}
                      onFocus={() => setShowNationalityDropdown(true)}
                      required
                    />
                    {showNationalityDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000
                      }}>
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <div
                              key={country.code}
                              onClick={() => handleNationalitySelect(country.name)}
                              style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <span style={{ fontSize: '16px' }}>{country.flag}</span>
                              <span>{country.name}</span>
                            </div>
                          ))
                        ) : (
                          <div style={{
                            padding: '12px 16px',
                            color: '#64748b',
                            fontSize: '14px',
                            textAlign: 'center'
                          }}>
                            No countries found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">National ID / Passport Number *</label>
                  <input
                    type="text"
                    name="id_passport"
                    className="form-input"
                    placeholder="Enter ID or passport number"
                    value={formData.id_passport}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">KRA PIN Number</label>
                  <input
                    type="text"
                    name="kra_pin"
                    className="form-input"
                    placeholder="Enter KRA PIN (e.g., A001234567P)"
                    value={formData.kra_pin}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Category Section */}
            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '16px', borderBottom: '2px solid #037b90', paddingBottom: '8px' }}>
                Recruiter Category
              </h4>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <select
                    name="category_id"
                    className="form-input"
                    value={formData.category_id}
                    onChange={handleInputChange}
                  >
                    <option value="">Auto (based on email domain)</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>{cat.display_name}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Location Information Section */}
            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '16px', borderBottom: '2px solid #037b90', paddingBottom: '8px' }}>
                Location Information
              </h4>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">County *</label>
                  <div style={{ position: 'relative' }} className="county-dropdown">
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search county..."
                      value={countySearch}
                      onChange={(e) => {
                        setCountySearch(e.target.value);
                        setShowCountyDropdown(true);
                      }}
                      onFocus={() => setShowCountyDropdown(true)}
                      required
                    />
                    {showCountyDropdown && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000
                      }}>
                        {filteredCounties.length > 0 ? (
                          filteredCounties.map((county) => (
                            <div
                              key={county.code}
                              onClick={() => handleCountySelect(county.name)}
                              style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <span style={{ fontSize: '16px' }}><Icon name="landmark" /></span>
                              <div>
                                <div style={{ fontWeight: '600' }}>{county.name}</div>
                                <div style={{ fontSize: '12px', color: '#64748b' }}>Capital: {county.capital}</div>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div style={{
                            padding: '12px 16px',
                            color: '#64748b',
                            fontSize: '14px',
                            textAlign: 'center'
                          }}>
                            No counties found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Sub County</label>
                  <div style={{ position: 'relative' }} className="subcounty-dropdown">
                    <input
                      type="text"
                      className="form-input"
                      placeholder={formData.county ? " Search sub county..." : "Select county first"}
                      value={subcountySearch}
                      onChange={(e) => {
                        setSubcountySearch(e.target.value);
                        setShowSubcountyDropdown(true);
                      }}
                      onFocus={() => formData.county && setShowSubcountyDropdown(true)}
                      disabled={!formData.county}
                    />
                    {showSubcountyDropdown && formData.county && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        zIndex: 1000
                      }}>
                        {filteredSubcounties.length > 0 ? (
                          filteredSubcounties.map((subcounty) => (
                            <div
                              key={subcounty}
                              onClick={() => handleSubcountySelect(subcounty)}
                              style={{
                                padding: '12px 16px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #f1f5f9',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '14px',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                            >
                              <span style={{ fontSize: '16px' }}><Icon name="map-pin" /></span>
                              <span>{subcounty}</span>
                            </div>
                          ))
                        ) : (
                          <div style={{
                            padding: '12px 16px',
                            color: '#64748b',
                            fontSize: '14px',
                            textAlign: 'center'
                          }}>
                            No sub counties found
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Location</label>
                  <input
                    type="text"
                    name="location"
                    className="form-input"
                    placeholder="Enter location"
                    value={formData.location}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Sublocation</label>
                  <input
                    type="text"
                    name="sublocation"
                    className="form-input"
                    placeholder="Enter sublocation"
                    value={formData.sublocation}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Village</label>
                  <input
                    type="text"
                    name="village"
                    className="form-input"
                    placeholder="Enter village name"
                    value={formData.village}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '16px', borderBottom: '2px solid #037b90', paddingBottom: '8px' }}>
                Contact Information
              </h4>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Mobile Telephone Number *</label>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {/* Country Code Dropdown */}
                    <div style={{ position: 'relative', minWidth: '140px' }} className="phone-country-dropdown">
                      <div
                        className="form-input"
                        onClick={() => setShowPhoneCountryDropdown(true)}
                        style={{
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px',
                          minWidth: '140px'
                        }}
                      >
                        <span style={{ fontSize: '16px' }}>{selectedPhoneCountry.flag}</span>
                        <span style={{ fontSize: '14px', fontWeight: '500' }}>{selectedPhoneCountry.dialCode}</span>
                        <span style={{ fontSize: '12px', marginLeft: 'auto' }}>▼</span>
                      </div>
                      {showPhoneCountryDropdown && (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #e2e8f0',
                          borderRadius: '8px',
                          boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)',
                          maxHeight: '200px',
                          overflowY: 'auto',
                          zIndex: 1000,
                          minWidth: '280px'
                        }}>
                          <div style={{ padding: '8px' }}>
                            <input
                              type="text"
                              placeholder="Search country or code..."
                              value={phoneCountrySearch}
                              onChange={(e) => setPhoneCountrySearch(e.target.value)}
                              style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #e2e8f0',
                                borderRadius: '6px',
                                fontSize: '14px',
                                outline: 'none'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          {filteredPhoneCountries.length > 0 ? (
                            filteredPhoneCountries.map((country) => (
                              <div
                                key={country.code}
                                onClick={() => handlePhoneCountrySelect(country)}
                                style={{
                                  padding: '12px 16px',
                                  cursor: 'pointer',
                                  borderBottom: '1px solid #f1f5f9',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '12px',
                                  fontSize: '14px',
                                  transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                              >
                                <span style={{ fontSize: '16px' }}>{country.flag}</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontWeight: '500' }}>{country.name}</div>
                                  <div style={{ fontSize: '12px', color: '#64748b' }}>{country.dialCode}</div>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div style={{
                              padding: '12px 16px',
                              color: '#64748b',
                              fontSize: '14px',
                              textAlign: 'center'
                            }}>
                              No countries found
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Phone Number Input */}
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="712345678"
                      value={phoneNumber}
                      onChange={handlePhoneNumberChange}
                      style={{ flex: 1 }}
                      required
                    />
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    Full number: {selectedPhoneCountry.dialCode} {phoneNumber}
                  </div>
                </div>
                <div className="form-group">
                  <EmailInput
                    label="Email Address"
                    name="email"
                    value={formData.email}
                    onChange={handleEmailChange}
                    onValidationChange={handleEmailValidationChange}
                    placeholder="recruiter@example.com"
                    required={true}
                    showValidation={true}
                    readOnly={isEditMode}
                  />
                  {isEditMode && (
                    <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      Email cannot be changed as it's used as the login identifier
                    </p>
                  )}
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Residential Address *</label>
                  <textarea
                    name="residential_address"
                    className="form-input"
                    rows="3"
                    placeholder="Estate/Location, House/Plot No., Street (e.g., Westlands Estate, Plot 123, Waiyaki Way)"
                    value={formData.residential_address}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Postal Address</label>
                  <input
                    type="text"
                    name="postal_address"
                    className="form-input"
                    placeholder="P.O. Box 12345-00100, Nairobi"
                    value={formData.postal_address}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>

            {/* Payment Information Section */}
            <div style={{ marginBottom: '32px' }}>
              <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '16px', borderBottom: '2px solid #037b90', paddingBottom: '8px' }}>
                Payment Information
              </h4>
              <div className="form-grid">
                <div className="form-group">
                  <label className="form-label">Bank Name</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Search bank..."
                      value={bankSearch}
                      onChange={(e) => {
                        setBankSearch(e.target.value);
                        setShowBankDropdown(true);
                      }}
                      onFocus={() => setShowBankDropdown(true)}
                      required
                    />
                    {showBankDropdown && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0,
                        backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', maxHeight: '200px', overflowY: 'auto', zIndex: 1000
                      }}>
                        {filteredBanks.length > 0 ? (
                          filteredBanks.map((bank) => (
                            <div key={bank.bank_code} onClick={() => handleBankSelect(bank.bank_name)}
                              style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s', fontSize: '14px' }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}>
                              {bank.bank_name}
                            </div>
                          ))
                        ) : (<div style={{ padding: '12px 16px', color: '#64748b', textAlign: 'center', fontSize: '14px' }}>No banks found</div>)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Branch Name / Code</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      className="form-input"
                      placeholder={formData.bank_name ? " Search branch..." : "Select bank first"}
                      value={branchSearch}
                      onChange={(e) => {
                        setBranchSearch(e.target.value);
                        setShowBranchDropdown(true);
                      }}
                      onFocus={() => formData.bank_name && setShowBranchDropdown(true)}
                      disabled={!formData.bank_name}
                      required
                    />
                    {showBranchDropdown && formData.bank_name && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0,
                        backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: '8px',
                        boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)', maxHeight: '200px', overflowY: 'auto', zIndex: 1000
                      }}>
                        {filteredBranches.length > 0 ? (
                          filteredBranches.map((branch) => (
                            <div key={branch.branch_code} onClick={() => handleBranchSelect(branch.branch_code, branch.branch_name)}
                              style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f1f5f9', transition: 'background-color 0.2s', fontSize: '14px' }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'} onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}>
                              <div style={{ fontWeight: '600' }}>{branch.branch_name}</div>
                              <div style={{ fontSize: '12px', color: '#64748b' }}>Code: {branch.branch_code}</div>
                            </div>
                          ))
                        ) : (<div style={{ padding: '12px 16px', color: '#64748b', textAlign: 'center', fontSize: '14px' }}>No branches found</div>)}
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Account Number</label>
                  <input
                    type="text"
                    name="bank_account"
                    className="form-input"
                    placeholder="Enter account number"
                    value={formData.bank_account}
                    onChange={handleInputChange}
                    disabled={!formData.branch_code}
                    required
                  />
                </div>
              </div>
              <div style={{
                marginTop: '16px',
                padding: '12px',
                background: '#f0f9ff',
                borderRadius: '8px',
                border: '1px solid #0ea5e9'
              }}>
                <p style={{ fontSize: '13px', color: '#0369a1', margin: 0 }}>
                  <strong>Note:</strong> Provide a bank account number for commission payments.
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '16px', marginTop: '32px', paddingTop: '24px', borderTop: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submitting}
              >
                {submitting
                  ? (isEditMode ? 'Updating Recruiter...' : 'Creating Recruiter...')
                  : (isEditMode ? 'Update Recruiter' : 'Create Recruiter')
                }
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={handleCancel}
                disabled={submitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">
              All Recruiters ({pagination?.total || 0}
              {pagination?.total !== recruiters.length && (
                <span style={{ color: '#64748b', fontWeight: '400' }}> of {pagination?.total || 0}</span>
              )})
            </h2>
            <p className="card-subtitle">
              {countyFilter || searchTerm ? (
                <>Filtered recruiters with performance metrics • {countyFilter && `County: ${countyFilter}`} {searchTerm && `Search: "${searchTerm}"`}</>
              ) : (
                'Complete list of registered recruiters with performance metrics'
              )}
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            {(countyFilter || searchTerm) && (
              <button
                className="btn btn-outline btn-sm"
                onClick={() => {
                  setSearchTerm('');
                  setCountyFilter('');
                  setCountyFilterSearch('');
                }}
                style={{ color: '#6b7280' }}
              >
                � Clear Filters
              </button>
            )}
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                // Export recruiters data (filtered or all)
                const dataToExport = displayRecruiters.length > 0 ? displayRecruiters : recruiters;
                const csvContent = [
                  'Name,Email,Referral Code,Status,Admissions,Enrollments,Commission',
                  ...dataToExport.map(r => `${r.full_name},${r.email},${r.referral_code},${r.status},${r.admission_count || 0},${r.enrollment_count || 0},${r.total_commission || 0}`)
                ].join('\n');

                const blob = new Blob([csvContent], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                const filename = (countyFilter || searchTerm) ?
                  `recruiters_filtered_export_${new Date().toISOString().split('T')[0]}.csv` :
                  `recruiters_export_${new Date().toISOString().split('T')[0]}.csv`;
                link.download = filename;
                link.click();
                URL.revokeObjectURL(url);

                toast.success(`${dataToExport.length} recruiters exported successfully!`);
              }}
            >
              <Icon name="download" /> Export Data
            </button>
          </div>
        </div>
        <>
          <div className="table-container">
            <table className="table">
              {/* ... table content remains same ... */}
              <thead>
                <tr>
                  <th>Referral Code</th>
                  <th>Recruiter</th>
                  <th>Contact</th>
                  <th>Location</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayRecruiters.length > 0 ? (
                  displayRecruiters.map((recruiter, index) => (
                      <tr key={recruiter.id} onClick={() => navigate(`/admin/recruiters/${recruiter.id}`)} style={{ cursor: 'pointer' }}>
                        <td>
                          <span className="badge badge-info" style={{ fontSize: '13px', fontWeight: '700' }}>
                            {recruiter.referral_code}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontWeight: '600', fontSize: '15px' }}>{recruiter.full_name || 'Unknown'}</div>
                          <div style={{ fontSize: '13px', color: '#64748b' }}>{recruiter.email}</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>{recruiter.mobile_number}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>Primary contact</div>
                        </td>
                        <td>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>{recruiter.county}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>{recruiter.subcounty || 'Not specified'}</div>
                        </td>
                        <td>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>
                            {recruiter.category_display_name || recruiter.category_name || 'Uncategorized'}
                          </span>
                        </td>
                        <td>
                          <span className={`badge ${recruiter.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                            {recruiter.status === 'active' ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={(e) => { e.stopPropagation(); navigate(`/admin/recruiters/${recruiter.id}`); }}
                          >
                            View
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={(e) => { e.stopPropagation(); handleEdit(recruiter); }}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={(e) => { e.stopPropagation(); handleDelete(recruiter.id); }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" style={{ textAlign: 'center', padding: '60px 20px' }}>
                      <div style={{ color: '#64748b' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}><Icon name="search" /></div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>No recruiters found</h3>
                        <p style={{ fontSize: '14px', marginBottom: '20px' }}>
                          {countyFilter || searchTerm ? 'No recruiters match your current filters.' : 'No recruiters have been added to the system yet.'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <Pagination
            pagination={pagination}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            loading={loading}
          />
        </>
      </div>
    </div>
  );
}
function PaymentsOverview() {
  const [stats, setStats] = useState(null);
  const [recentUploads, setRecentUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPaymentsData();
  }, []);

  const loadPaymentsData = async () => {
    try {
      const [statsResponse, uploadsResponse] = await Promise.all([
        paymentUploadAPI.getStats(),
        paymentUploadAPI.getAll()
      ]);
      setStats(statsResponse.data);
      setRecentUploads(uploadsResponse.data.slice(0, 5));
    } catch (error) {
      toast.error('Failed to load payments data');
    } finally {
      setLoading(false);
    }
  };

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="dollar" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Payments...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching payment records and financial data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content" style={{ position: 'relative', minHeight: '800px' }}>
      <style>
        {`
          @keyframes pulse-amber {
            0% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0.4); }
            70% { box-shadow: 0 0 0 6px rgba(245, 158, 11, 0); }
            100% { box-shadow: 0 0 0 0 rgba(245, 158, 11, 0); }
          }
        `}
      </style>

      <div style={{
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(255, 255, 255, 0.5)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 50,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '24px',
        margin: '16px'
      }}>
        <div style={{
          textAlign: 'center',
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '48px',
          borderRadius: '24px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.15)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          maxWidth: '440px',
          transform: 'translateY(-40px)'
        }}>
          <div style={{
            width: '80px', height: '80px',
            background: 'linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%)',
            borderRadius: '24px',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 24px',
            boxShadow: '0 10px 25px -5px rgba(203, 213, 225, 0.5)',
            transform: 'rotate(-5deg)'
          }}>
            <span style={{ fontSize: '32px', transform: 'rotate(5deg)', display: 'block' }}><Icon name="tool" /></span>
          </div>
          <h2 style={{ fontSize: '24px', fontWeight: '800', color: '#037b90', marginBottom: '12px', letterSpacing: '-0.5px' }}>
            Under Maintenance
          </h2>
          <p style={{ color: '#64748b', fontSize: '15px', lineHeight: '1.6', marginBottom: '32px' }}>
            We're currently upgrading the Payments Management architecture. This module will be back online shortly with enhanced capabilities.
          </p>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            padding: '8px 16px', background: '#fffbeb', border: '1px solid #fef3c7',
            borderRadius: '20px', color: '#92400e', fontSize: '13px', fontWeight: '600'
          }}>
            <div style={{
              width: '8px', height: '8px', background: '#f59e0b',
              borderRadius: '50%', animation: 'pulse-amber 2s infinite'
            }}></div>
            Engineer at work
          </div>
        </div>
      </div>

      <div style={{ pointerEvents: 'none', userSelect: 'none' }}>
        <div className="page-header">
          <div>
            <h1 className="page-title">Payments Management</h1>
            <p className="page-subtitle">Comprehensive payment processing, upload management, and financial analytics</p>
          </div>
          <div style={{ display: 'flex', gap: '16px' }}>
            <Link to="/admin/payments/reports" className="btn btn-secondary btn-sm">Generate Reports</Link>
            <Link to="/admin/payments/uploads" className="btn btn-primary btn-sm">Upload Payments</Link>
          </div>
        </div>

        {/* Enhanced Payment Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-title">Total Processed</div>
              <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}><Icon name="dollar" /></div>
            </div>
          <div className="stat-card-value">KES {stats?.totalPaymentsProcessed?.toLocaleString() || 0}</div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-title">Recruiters Paid</div>
              <div className="stat-card-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}><Icon name="users" /></div>
            </div>
            <div className="stat-card-value">{stats?.totalRecruiterspaid || 0}</div>
            <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
              Active recipients
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-card-title">Average Payment</div>
              <div className="stat-card-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}><Icon name="bar-chart" /></div>
            </div>
          <div className="stat-card-value">KES {stats?.averagePaymentAmount?.toLocaleString() || 0}</div>
          </div>
        </div>

        {/* Quick Actions Panel */}
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">Quick Actions</h2>
              <p className="card-subtitle">Common payment operations and management tasks</p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
            <Link to="/admin/payments/uploads" className="btn btn-primary" style={{
              justifyContent: 'flex-start',
              textDecoration: 'none'
            }}>
              <Icon name="upload" /> Upload Payment CSV
            </Link>

            <Link to="/admin/payments/recruiters" className="btn btn-secondary" style={{
              justifyContent: 'flex-start',
              textDecoration: 'none'
            }}>
              <Icon name="users" /> Recruiter Payments
            </Link>

            <Link to="/admin/payments/reports" className="btn btn-outline" style={{
              justifyContent: 'flex-start',
              textDecoration: 'none'
            }}>
              <Icon name="bar-chart" /> Generate Reports
            </Link>

            <button
              className="btn btn-outline"
              style={{
                justifyContent: 'flex-start'
              }}
              onClick={() => {
                downloadCSVTemplate();
                toast.success('Payment CSV template downloaded successfully!');
              }}
            >
              <Icon name="download" /> Download Template
            </button>
          </div>
        </div>

        {/* Recent Uploads Section */}
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">Recent Payment Uploads</h2>
              <p className="card-subtitle">Latest CSV uploads and processing status</p>
            </div>
            <Link to="/admin/payments/uploads" className="btn btn-outline btn-sm">View All</Link>
          </div>

          {recentUploads.length > 0 ? (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Upload Date</th>
                    <th>Records</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recentUploads.map((upload) => (
                    <tr key={upload.id}>
                      <td>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>{upload.filename}</div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>{upload.notes}</div>
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>
                            {new Date(upload.upload_date).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {formatTimeAgo(upload.upload_date)}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontSize: '16px', fontWeight: '700' }}>{upload.total_records}</span>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            <div><Icon name="check-circle" /> {upload.successful_records}</div>
                            <div><Icon name="x-circle" /> {upload.failed_records}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: '700', color: '#10b981', fontSize: '16px' }}>
                          KES {upload.total_amount.toLocaleString()}
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${upload.upload_status === 'completed' ? 'badge-success' :
                          upload.upload_status === 'processing' ? 'badge-warning' : 'badge-danger'
                          }`}>
                          {upload.upload_status === 'completed' ? 'Completed' :
                            upload.upload_status === 'processing' ? 'Processing' : 'Failed'}
                        </span>
                      </td>
                      <td>
                        <Link
                          to={`/admin/payments/uploads/${upload.id}`}
                          className="btn btn-outline btn-sm"
                        >
                          View Details
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}><Icon name="upload" /></div>
              <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Payment Uploads Yet</h3>
              <p style={{ fontSize: '14px', marginBottom: '24px' }}>
                Upload your first CSV file with payment records to get started
              </p>
              <Link to="/admin/payments/uploads" className="btn btn-primary">Upload Payment CSV</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Payment Upload Management Component
function PaymentUploads() {
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadUploads();
  }, []);

  const loadUploads = async () => {
    try {
      const response = await paymentUploadAPI.getAll();
      setUploads(response.data);
    } catch (error) {
      toast.error('Failed to load uploads');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      await paymentUploadAPI.upload(formData);
      toast.success('Payment file uploaded successfully! Processing...');
      loadUploads();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="upload" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Payment Uploads...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Payment Uploads</h1>
          <p className="page-subtitle">Upload and manage CSV files containing recruiter payment records</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Link to="/admin/payments" className="btn btn-outline btn-sm">← Back to Payments</Link>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => {
              downloadCSVTemplate();
              toast.success('CSV template downloaded successfully!');
            }}
          >
            Download Template
          </button>
          <label className={`btn btn-primary btn-sm ${uploading ? 'loading' : ''}`}>
            {uploading ? 'Uploading...' : <><Icon name="upload" />{" Upload CSV"}</>}
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              disabled={uploading}
            />
          </label>
        </div>
      </div>

      {/* Upload Instructions */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">CSV Upload Instructions</h2>
            <p className="card-subtitle">Required format for payment upload files</p>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '12px' }}>
              Required Columns
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>recruiter_email:</strong> Recruiter's email address
              </li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>referral_code:</strong> Recruiter's referral code
              </li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>amount_paid:</strong> Payment amount (positive number)
              </li>
              <li style={{ padding: '8px 0', fontSize: '14px' }}>
                <strong>payment_method:</strong> M-Pesa, Bank Transfer, etc.
              </li>
            </ul>
          </div>

          <div>
            <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#037b90', marginBottom: '12px' }}>
              Optional Columns
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>payment_reference:</strong> Transaction reference
              </li>
              <li style={{ padding: '8px 0', borderBottom: '1px solid #e2e8f0', fontSize: '14px' }}>
                <strong>payment_date:</strong> Date of payment (YYYY-MM-DD)
              </li>
              <li style={{ padding: '8px 0', fontSize: '14px' }}>
                <strong>notes:</strong> Additional notes
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Uploads Table */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Upload History</h2>
            <p className="card-subtitle">All payment file uploads and their processing status</p>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>File Details</th>
                <th>Upload Info</th>
                <th>Processing Results</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {uploads.map((upload) => (
                <tr key={upload.id}>
                  <td>
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                        {upload.filename}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {upload.notes}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontSize: '14px', fontWeight: '500' }}>
                        {new Date(upload.upload_date).toLocaleDateString()}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {new Date(upload.upload_date).toLocaleTimeString()}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#64748b' }}>{upload.total_records}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Total</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>{upload.successful_records}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Success</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#ef4444' }}>{upload.failed_records}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Failed</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                      KES {upload.total_amount.toLocaleString()}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${upload.upload_status === 'completed' ? 'badge-success' :
                      upload.upload_status === 'processing' ? 'badge-warning' : 'badge-danger'
                      }`}>
                      {upload.upload_status === 'completed' ? 'Completed' :
                        upload.upload_status === 'processing' ? 'Processing' : 'Failed'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Link
                        to={`/admin/payments/uploads/${upload.id}`}
                        className="btn btn-outline btn-sm"
                      >
                        View Details
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Placeholder components for other payment features
function UploadDetails() {
  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Upload Details</h1>
          <p className="page-subtitle">Detailed view of payment upload processing</p>
        </div>
        <Link to="/admin/payments/uploads" className="btn btn-outline btn-sm">← Back to Uploads</Link>
      </div>
      <div className="card">
        <p>Upload details implementation would go here...</p>
      </div>
    </div>
  );
}


function RecruiterPayments() {
  const [recruiters, setRecruiters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name');

  useEffect(() => {
    loadRecruiterPayments();
  }, [searchTerm, sortBy]);

  const loadRecruiterPayments = async () => {
    try {
      const response = await paymentUploadAPI.getRecruiterPayments(searchTerm, sortBy);
      setRecruiters(response.data);
    } catch (error) {
      toast.error('Failed to load recruiter payments');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="users" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Recruiter Payments...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Recruiter Payments</h1>
          <p className="page-subtitle">View payment history and status for all recruiters</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Link to="/admin/payments" className="btn btn-outline btn-sm">← Back to Payments</Link>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => {
              downloadRecruiterPaymentsCSV(recruiters);
              toast.success('Recruiter payments exported successfully!');
            }}
          >
            Export All
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '32px' }}>
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Recruiters</div>
            <div className="stat-card-icon" style={{ background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6' }}><Icon name="users" /></div>
          </div>
          <div className="stat-card-value">{recruiters.length}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Active recruiters
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Earned</div>
            <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}><Icon name="dollar" /></div>
          </div>
          <div className="stat-card-value">
            KES {Number(recruiters.reduce((sum, r) => sum + r.total_commissions_earned, 0)).toFixed(2)}
          </div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Total commissions
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Paid</div>
            <div className="stat-card-icon" style={{ background: 'rgba(3, 123, 144, 0.1)', color: '#037b90' }}><Icon name="check-circle" /></div>
          </div>
          <div className="stat-card-value">
            KES {recruiters.reduce((sum, r) => sum + r.total_paid, 0).toLocaleString()}
          </div>
          <div style={{ fontSize: '13px', color: '#037b90', fontWeight: '600', marginTop: '8px' }}>
            Payments processed
          </div>
        </div>


      </div>

      {/* Search and Filters */}
      <div className="card">
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ flex: '1', minWidth: '300px' }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name, email, or referral code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <select
              className="form-select"
              style={{ minWidth: '150px' }}
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
            >
              <option value="name">Sort by Name</option>
              <option value="total_earned">Total Earned</option>
              <option value="total_paid">Total Paid</option>
              <option value="pending">Pending Amount</option>
              <option value="last_payment">Last Payment</option>
            </select>
          </div>
        </div>
      </div>

      {/* Recruiters Table */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Recruiter Payment Overview</h2>
            <p className="card-subtitle">Commission earnings and payment status for all recruiters</p>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Recruiter</th>
                <th>Performance</th>
                <th>Total Earned</th>
                <th>Total Paid</th>
                <th>Pending</th>
                <th>Last Payment</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {recruiters.map((recruiter) => (
                <tr key={recruiter.recruiter_id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, #3b82f6, #10b981)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '14px'
                      }}>
                        {recruiter.recruiter_name.split(' ').map(n => n[0]).join('')}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{recruiter.recruiter_name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{recruiter.recruiter_email}</div>
                        <span className="badge badge-info" style={{ fontSize: '10px', marginTop: '2px' }}>
                          {recruiter.referral_code}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#3b82f6' }}>{recruiter.admissions_count}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Admissions</div>
                      </div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#10b981' }}>{recruiter.enrollments_count}</div>
                        <div style={{ fontSize: '11px', color: '#64748b' }}>Enrollments</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                        KES {Number(recruiter.total_commissions_earned || 0).toFixed(2)}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        Total commissions
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: '700', color: '#10b981', fontSize: '16px' }}>
                        KES {recruiter.total_paid.toLocaleString()}
                      </div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {recruiter.payment_count} payments
                      </div>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{
                        fontWeight: '700',
                        color: recruiter.pending_amount > 0 ? '#f59e0b' : '#64748b',
                        fontSize: '16px'
                      }}>
                        KES {recruiter.pending_amount.toLocaleString()}
                      </div>
                      {recruiter.pending_amount > 0 && (
                        <div style={{ fontSize: '12px', color: '#f59e0b' }}>
                          Needs payment
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      {recruiter.last_payment_date ? (
                        <>
                          <div style={{ fontWeight: '500', fontSize: '14px' }}>
                            {new Date(recruiter.last_payment_date).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {Math.floor((new Date() - new Date(recruiter.last_payment_date)) / (1000 * 60 * 60 * 24))} days ago
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: '14px', color: '#94a3b8' }}>No payments yet</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <Link
                        to={`/admin/payments/recruiters/${recruiter.recruiter_id}`}
                        className="btn btn-outline btn-sm"
                      >
                        View Details
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function RecruiterPaymentDetails() {
  const { id } = useParams();
  const [recruiter, setRecruiter] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadRecruiterDetails();
  }, [id]);

  const loadRecruiterDetails = async () => {
    try {
      const response = await paymentUploadAPI.getRecruiterPaymentDetails(id);
      setRecruiter(response.data);
    } catch (error) {
      toast.error('Failed to load recruiter details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="user" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Recruiter Details...</h3>
        </div>
      </div>
    );
  }

  if (!recruiter) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Recruiter not found</h3>
          <Link to="/admin/payments/recruiters" className="btn btn-primary" style={{ marginTop: '16px' }}>
            Back to Recruiters
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">{recruiter.recruiter_name}</h1>
          <p className="page-subtitle">Detailed payment history and commission breakdown</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Link to="/admin/payments/recruiters" className="btn btn-outline btn-sm">← Back to List</Link>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              downloadRecruiterPaymentHistory(recruiter);
              toast.success(`Payment history for ${recruiter.recruiter_name} exported successfully!`);
            }}
          >
            Export History
          </button>
        </div>
      </div>

      {/* Recruiter Summary */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '24px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #10b981)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontSize: '32px',
            fontWeight: '700'
          }}>
            {recruiter.recruiter_name.split(' ').map(n => n[0]).join('')}
          </div>
          <div style={{ flex: 1 }}>
            <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '8px' }}>
              {recruiter.recruiter_name}
            </h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>{recruiter.recruiter_email}</span>
              <span className="badge badge-info">{recruiter.referral_code}</span>
            </div>
            <div style={{ display: 'flex', gap: '32px' }}>
              <div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#3b82f6' }}>
                  {recruiter.admissions_count}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Admissions</div>
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#10b981' }}>
                  {recruiter.enrollments_count}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Enrollments</div>
              </div>
              <div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#037b90' }}>
                  KES {Number(recruiter.total_commissions_earned || 0).toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600' }}>Total Earned</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Summary Stats */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', marginBottom: '32px' }}>
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Earned</div>
            <div className="stat-card-icon" style={{ background: 'rgba(3, 123, 144, 0.1)', color: '#037b90' }}><Icon name="dollar" /></div>
          </div>
          <div className="stat-card-value">KES {Number(recruiter.total_commissions_earned || 0).toFixed(2)}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Commission earnings
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Paid</div>
            <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}><Icon name="check-circle" /></div>
          </div>
          <div className="stat-card-value">KES {recruiter.total_paid.toLocaleString()}</div>
          <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '600', marginTop: '8px' }}>
            {Math.round((recruiter.total_paid / recruiter.total_commissions_earned) * 100)}% of earned
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Pending Amount</div>
            <div className="stat-card-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}><Icon name="clock" /></div>
          </div>
          <div className="stat-card-value">KES {recruiter.pending_amount.toLocaleString()}</div>
          <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginTop: '8px' }}>
            Awaiting payment
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Payment Count</div>
            <div className="stat-card-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}><Icon name="bar-chart" /></div>
          </div>
          <div className="stat-card-value">{recruiter.payment_count}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Total payments made
          </div>
        </div>
      </div>

      {/* Payment History */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Payment History</h2>
            <p className="card-subtitle">Complete record of all payments made to this recruiter</p>
          </div>
        </div>

        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>Payment Date</th>
                <th>Amount</th>
                <th>Method</th>
                <th>Reference</th>
                <th>Status</th>
                <th>Upload Batch</th>
              </tr>
            </thead>
            <tbody>
              {recruiter.payment_history.map((payment) => (
                <tr key={payment.id}>
                  <td>
                    <div>
                      {payment.payment_date ? (
                        <>
                          <div style={{ fontWeight: '600', fontSize: '14px' }}>
                            {new Date(payment.payment_date).toLocaleDateString()}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {Math.floor((new Date() - new Date(payment.payment_date)) / (1000 * 60 * 60 * 24))} days ago
                          </div>
                        </>
                      ) : (
                        <div style={{ fontSize: '14px', color: '#94a3b8' }}>Pending</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontWeight: '700', color: '#037b90', fontSize: '16px' }}>
                      KES {payment.amount.toLocaleString()}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                      {payment.payment_method || 'Not specified'}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '13px', fontFamily: 'monospace', color: '#64748b' }}>
                      {payment.reference || '-'}
                    </div>
                  </td>
                  <td>
                    <span className={`badge ${payment.status === 'completed' ? 'badge-success' :
                      payment.status === 'pending' ? 'badge-warning' : 'badge-danger'
                      }`}>
                      {payment.status === 'completed' ? 'Completed' :
                        payment.status === 'pending' ? 'Pending' : 'Failed'}
                    </span>
                  </td>
                  <td>
                    {payment.upload_id ? (
                      <Link
                        to={`/admin/payments/uploads/${payment.upload_id}`}
                        className="btn btn-outline btn-sm"
                        style={{ fontSize: '11px', padding: '4px 8px' }}
                      >
                        Upload #{payment.upload_id}
                      </Link>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function PaymentReports() {
  const [reportType, setReportType] = useState('summary');
  const [dateRange, setDateRange] = useState('last_30_days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [narrative, setNarrative] = useState('');
  const [narrating, setNarrating] = useState(false);
  const [selectedRecruiters, setSelectedRecruiters] = useState([]);

  const reportTypes = [
    { value: 'summary', label: 'Payment Summary Report', description: 'Overview of all payments and commissions' },
    { value: 'detailed', label: 'Detailed Payment Report', description: 'Line-by-line payment transactions' },
    { value: 'recruiter_performance', label: 'Recruiter Performance Report', description: 'Individual recruiter earnings and metrics' },
    { value: 'monthly_trends', label: 'Monthly Trends Report', description: 'Payment trends and patterns over time' },
    { value: 'payment_methods', label: 'Payment Methods Report', description: 'Breakdown by payment channels' },
    { value: 'pending_payments', label: 'Pending Payments Report', description: 'Outstanding commission payments' },
    { value: 'audit_trail', label: 'Payment Audit Trail', description: 'Complete payment processing history' }
  ];

  const dateRanges = [
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'last_90_days', label: 'Last 90 Days' },
    { value: 'current_month', label: 'Current Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'current_quarter', label: 'Current Quarter' },
    { value: 'last_quarter', label: 'Last Quarter' },
    { value: 'current_year', label: 'Current Year' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const generateReport = async () => {
    setLoading(true);
    try {
      const params = { dateRange };
      if (dateRange === 'custom') {
        params.startDate = customStartDate;
        params.endDate = customEndDate;
      }

      const res = await reportAPI.getFinancialReport(params);
      setReportData({ ...res.data, reportType });

      toast.success(`${reportTypes.find(r => r.value === reportType)?.label} generated successfully!`);
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const generateMockReportData = (type, range) => {
    console.warn('generateMockReportData is deprecated; real data is fetched from /api/reports/financial');
    return { reportType: type };
  };

  const generateNarrative = async () => {
    if (!reportData) {
      toast.error('Please generate a report first');
      return;
    }
    setNarrating(true);
    try {
      const title = reportTypes.find((r) => r.value === reportType)?.label || 'Payment Report';
      const res = await aiAPI.reportNarrative({ title, dateRange, data: reportData });
      setNarrative(res.data?.narrative || 'No narrative generated.');
      toast.success('AI narrative generated');
    } catch (e) {
      toast.error(e.message || 'Failed to generate narrative');
    } finally {
      setNarrating(false);
    }
  };

  const clearNarrative = () => setNarrative('');

  const exportReport = (format) => {
    if (!reportData) {
      toast.error('Please generate a report first');
      return;
    }

    const reportName = reportTypes.find(r => r.value === reportType)?.label || 'Payment Report';
    const timestamp = new Date().toISOString().split('T')[0];

    if (format === 'csv') {
      downloadFinancialReport(reportData, reportName, reportType);
      toast.success(`${reportName} exported as CSV successfully!`);
    } else if (format === 'pdf') {
      // Simulate PDF generation
      toast.info('PDF export feature - would generate comprehensive PDF report with charts and graphs');
    } else if (format === 'excel') {
      // Simulate Excel generation
      toast.info('Excel export feature - would generate detailed Excel workbook with multiple sheets and pivot tables');
    }
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Payment Reports</h1>
          <p className="page-subtitle">Generate comprehensive financial reports and analytics</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <Link to="/admin/payments" className="btn btn-outline btn-sm">← Back to Payments</Link>
          {reportData && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => exportReport('csv')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Icon name="bar-chart" /> Export CSV
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => exportReport('excel')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Icon name="trending-up" /> Export Excel
              </button>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => exportReport('pdf')}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Icon name="file-text" /> Export PDF
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={generateNarrative}
                disabled={narrating}
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <Icon name="help" /> {narrating ? 'Generating…' : '✨ AI Narrative'}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Report Configuration */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Report Configuration</h2>
            <p className="card-subtitle">Select report type, date range, and filters</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
          {/* Report Type Selection */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              Report Type
            </label>
            <select
              className="form-select"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
            >
              {reportTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
              {reportTypes.find(r => r.value === reportType)?.description}
            </p>
          </div>

          {/* Date Range Selection */}
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
              Date Range
            </label>
            <select
              className="form-select"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              {dateRanges.map(range => (
                <option key={range.value} value={range.value}>{range.label}</option>
              ))}
            </select>
          </div>

          {/* Custom Date Range */}
          {dateRange === 'custom' && (
            <>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Start Date
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  End Date
                </label>
                <input
                  type="date"
                  className="form-input"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e2e8f0' }}>
          <button
            className={`btn btn-primary ${loading ? 'loading' : ''}`}
            onClick={generateReport}
            disabled={loading}
          >
            {loading ? 'Generating Report...' : <><Icon name="bar-chart" />{" Generate Report"}</>}
          </button>
        </div>
      </div>

      {/* Report Results */}
      {reportData && (
        <div className="card">
          <div className="card-header">
            <div>
              <h2 className="card-title">
                {reportTypes.find(r => r.value === reportType)?.label}
              </h2>
              <p className="card-subtitle">
                Generated on {new Date(reportData.generatedAt).toLocaleString()} •
                {dateRanges.find(d => d.value === dateRange)?.label}
              </p>
            </div>
          </div>

          {narrative && (
            <div style={{ marginBottom: '24px', padding: '16px', background: 'linear-gradient(135deg, rgba(139,92,246,0.06), rgba(59,130,246,0.06))', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-lg)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                <h3 style={{ margin: 0, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <Icon name="help" /> AI Narrative
                </h3>
                <button className="btn btn-outline btn-sm" onClick={clearNarrative}>Dismiss</button>
              </div>
              <p style={{ margin: 0, whiteSpace: 'pre-wrap', lineHeight: 1.6, color: 'var(--text-primary)' }}>{narrative}</p>
            </div>
          )}

          {/* Summary Report */}
          {reportData.reportType === 'summary' && (
            <div>
              <div className="stats-grid" style={{ marginBottom: '32px' }}>
                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-card-title">Total Earned</div>
                    <div className="stat-card-icon" style={{ background: 'rgba(3, 123, 144, 0.1)', color: '#037b90' }}><Icon name="dollar" /></div>
                  </div>
                  <div className="stat-card-value">KES {reportData.summary.totalCommissionsEarned.toLocaleString()}</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                    Commission earnings
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-card-title">Total Paid</div>
                    <div className="stat-card-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10b981' }}><Icon name="check-circle" /></div>
                  </div>
                  <div className="stat-card-value">KES {reportData.summary.totalPaymentsMade.toLocaleString()}</div>
                  <div style={{ fontSize: '13px', color: '#10b981', fontWeight: '600', marginTop: '8px' }}>
                    {reportData.summary.paymentSuccessRate}% success rate
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-card-title">Pending</div>
                    <div className="stat-card-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#f59e0b' }}><Icon name="clock" /></div>
                  </div>
                  <div className="stat-card-value">KES {reportData.summary.pendingPayments.toLocaleString()}</div>
                  <div style={{ fontSize: '13px', color: '#f59e0b', fontWeight: '600', marginTop: '8px' }}>
                    Awaiting payment
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-header">
                    <div className="stat-card-title">Top Method</div>
                    <div className="stat-card-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8b5cf6' }}><Icon name="smartphone" /></div>
                  </div>
                  <div className="stat-card-value" style={{ fontSize: '18px' }}>{reportData.summary.topPaymentMethod}</div>
                  <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
                    Most used method
                  </div>
                </div>
              </div>

              <div className="table-container">
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Monthly Breakdown</h3>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>Total Payments</th>
                      <th>Transactions</th>
                      <th>Active Recruiters</th>
                      <th>Average per Recruiter</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.monthlyBreakdown.map((month, index) => (
                      <tr key={index}>
                        <td style={{ fontWeight: '600' }}>{month.month}</td>
                        <td style={{ fontWeight: '700', color: '#037b90' }}>
                          KES {month.payments.toLocaleString()}
                        </td>
                        <td>{month.transactions}</td>
                        <td>{month.recruiters}</td>
                        <td style={{ fontWeight: '600' }}>
                          KES {Math.round(month.payments / month.recruiters).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Detailed Report */}
          {reportData.reportType === 'detailed' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Recruiter</th>
                    <th>Amount</th>
                    <th>Method</th>
                    <th>Reference</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.transactions.map((txn) => (
                    <tr key={txn.id}>
                      <td>
                        <div style={{ fontWeight: '500' }}>
                          {new Date(txn.date).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {new Date(txn.date).toLocaleTimeString()}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600' }}>{txn.recruiterName}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{txn.referralCode}</div>
                      </td>
                      <td style={{ fontWeight: '700', color: '#037b90' }}>
                        KES {txn.amount.toLocaleString()}
                      </td>
                      <td>{txn.method}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: '13px' }}>{txn.reference}</td>
                      <td>
                        <span className={`badge ${txn.status === 'Completed' ? 'badge-success' : 'badge-danger'}`}>
                          {txn.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recruiter Performance Report */}
          {reportData.reportType === 'recruiter_performance' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Recruiter</th>
                    <th>Total Earned</th>
                    <th>Total Paid</th>
                    <th>Pending</th>
                    <th>Payment Efficiency</th>
                    <th>Performance</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.recruiters.map((recruiter, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ fontWeight: '600' }}>{recruiter.name}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{recruiter.code}</div>
                      </td>
                      <td style={{ fontWeight: '700', color: '#037b90' }}>
                        KES {recruiter.earned.toLocaleString()}
                      </td>
                      <td style={{ fontWeight: '700', color: '#10b981' }}>
                        KES {recruiter.paid.toLocaleString()}
                      </td>
                      <td style={{ fontWeight: '700', color: '#f59e0b' }}>
                        KES {recruiter.pending.toLocaleString()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '60px',
                            height: '6px',
                            backgroundColor: '#e2e8f0',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${recruiter.efficiency}%`,
                              height: '100%',
                              backgroundColor: recruiter.efficiency > 90 ? '#10b981' : recruiter.efficiency > 80 ? '#f59e0b' : '#ef4444',
                              borderRadius: '3px'
                            }}></div>
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: '600' }}>{recruiter.efficiency}%</span>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${recruiter.efficiency > 90 ? 'badge-success' :
                          recruiter.efficiency > 80 ? 'badge-warning' : 'badge-danger'
                          }`}>
                          {recruiter.efficiency > 90 ? 'Excellent' :
                            recruiter.efficiency > 80 ? 'Good' : 'Needs Attention'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Monthly Trends Report */}
          {reportData.reportType === 'monthly_trends' && (
            <div>
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Payment Trends Analysis</h3>
                <div className="table-container">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Month</th>
                        <th>Total Payments</th>
                        <th>Transactions</th>
                        <th>Growth Rate</th>
                        <th>Active Recruiters</th>
                        <th>Avg per Transaction</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.trends?.map((trend, index) => (
                        <tr key={index}>
                          <td style={{ fontWeight: '600' }}>{trend.month}</td>
                          <td style={{ fontWeight: '700', color: '#037b90' }}>
                            KES {Math.round(trend.payments).toLocaleString()}
                          </td>
                          <td>{trend.transactions}</td>
                          <td>
                            <span style={{
                              color: trend.growthRate > 0 ? '#10b981' : '#ef4444',
                              fontWeight: '600'
                            }}>
                              {trend.growthRate > 0 ? '+' : ''}{trend.growthRate.toFixed(1)}%
                            </span>
                          </td>
                          <td>{trend.recruiters}</td>
                          <td style={{ fontWeight: '600' }}>
                            KES {Math.round(trend.payments / trend.transactions).toLocaleString()}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Payment Methods Report */}
          {reportData.reportType === 'payment_methods' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Payment Method</th>
                    <th>Transaction Count</th>
                    <th>Total Amount</th>
                    <th>Market Share</th>
                    <th>Success Rate</th>
                    <th>Avg Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.paymentMethods?.map((method, index) => (
                    <tr key={index}>
                      <td style={{ fontWeight: '600' }}>{method.method}</td>
                      <td>{method.count}</td>
                      <td style={{ fontWeight: '700', color: '#037b90' }}>
                        KES {method.amount.toLocaleString()}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{
                            width: '60px',
                            height: '6px',
                            backgroundColor: '#e2e8f0',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${(method.amount / reportData.paymentMethods.reduce((sum, m) => sum + m.amount, 0)) * 100}%`,
                              height: '100%',
                              backgroundColor: ['#3b82f6', '#10b981', '#f59e0b', '#8b5cf6'][index % 4],
                              borderRadius: '3px'
                            }}></div>
                          </div>
                          <span style={{ fontSize: '14px', fontWeight: '600' }}>
                            {((method.amount / reportData.paymentMethods.reduce((sum, m) => sum + m.amount, 0)) * 100).toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td>
                        <span style={{ color: method.successRate > 95 ? '#10b981' : '#f59e0b', fontWeight: '600' }}>
                          {method.successRate.toFixed(1)}%
                        </span>
                      </td>
                      <td style={{ fontWeight: '600' }}>
                        KES {Math.round(method.amount / method.count).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pending Payments Report */}
          {reportData.reportType === 'pending_payments' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Priority</th>
                    <th>Recruiter</th>
                    <th>Pending Amount</th>
                    <th>Days Outstanding</th>
                    <th>Action Required</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.pendingPayments?.map((payment, index) => (
                    <tr key={index}>
                      <td>
                        <span className={`badge ${payment.priority === 'High' ? 'badge-danger' :
                          payment.priority === 'Medium' ? 'badge-warning' : 'badge-info'
                          }`}>
                          {payment.priority}
                        </span>
                      </td>
                      <td>
                        <div style={{ fontWeight: '600' }}>{payment.recruiter}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{payment.code}</div>
                      </td>
                      <td style={{ fontWeight: '700', color: '#f59e0b' }}>
                        KES {payment.amount.toLocaleString()}
                      </td>
                      <td>
                        <span style={{
                          color: payment.days > 20 ? '#ef4444' : payment.days > 10 ? '#f59e0b' : '#64748b',
                          fontWeight: '600'
                        }}>
                          {payment.days} days
                        </span>
                      </td>
                      <td>
                        <button className="btn btn-outline btn-sm">
                          {payment.days > 15 ? 'Follow Up' : 'Schedule Payment'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Audit Trail Report */}
          {reportData.reportType === 'audit_trail' && (
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Timestamp</th>
                    <th>Action</th>
                    <th>User</th>
                    <th>Details</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.auditTrail?.slice(0, 15).map((audit, index) => (
                    <tr key={index}>
                      <td>
                        <div style={{ fontWeight: '500' }}>
                          {new Date(audit.timestamp).toLocaleDateString()}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>
                          {new Date(audit.timestamp).toLocaleTimeString()}
                        </div>
                      </td>
                      <td style={{ fontWeight: '600' }}>{audit.action}</td>
                      <td style={{ fontSize: '13px', color: '#64748b' }}>{audit.user}</td>
                      <td>{audit.details}</td>
                      <td>
                        <span className={`badge ${audit.status === 'Success' ? 'badge-success' : 'badge-danger'}`}>
                          {audit.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Quick Report Templates */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">Quick Report Templates</h2>
            <p className="card-subtitle">Pre-configured reports for common use cases</p>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          <button
            className="btn btn-outline"
            style={{
              padding: '16px',
              textAlign: 'left',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
            onClick={() => {
              setReportType('summary');
              setDateRange('current_month');
              generateReport();
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: '600' }}><Icon name="bar-chart" /> Monthly Summary</div>
            {/* <div style={{ fontSize: '13px', color: '#64748b' }}>Current month payment overview</div> */}
          </button>

          <button
            className="btn btn-outline"
            style={{
              padding: '16px',
              textAlign: 'left',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
            onClick={() => {
              setReportType('pending_payments');
              setDateRange('last_30_days');
              generateReport();
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: '600' }}><Icon name="clock" /> Outstanding Payments</div>
            {/* <div style={{ fontSize: '13px', color: '#64748b' }}>All pending commission payments</div> */}
          </button>

          <button
            className="btn btn-outline"
            style={{
              padding: '16px',
              textAlign: 'left',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
            onClick={() => {
              setReportType('recruiter_performance');
              setDateRange('current_quarter');
              generateReport();
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: '600' }}><Icon name="trophy" /> Top Performers</div>
            {/* <div style={{ fontSize: '13px', color: '#64748b' }}>Quarterly recruiter rankings</div> */}
          </button>

          <button
            className="btn btn-outline"
            style={{
              padding: '16px',
              textAlign: 'left',
              height: 'auto',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: '8px'
            }}
            onClick={() => {
              setReportType('audit_trail');
              setDateRange('last_7_days');
              generateReport();
            }}
          >
            <div style={{ fontSize: '16px', fontWeight: '600' }}><Icon name="search" /> Recent Activity</div>
            {/* <div style={{ fontSize: '13px', color: '#64748b' }}>Last 7 days payment audit</div> */}
          </button>
        </div>
      </div>
    </div>
  );
}
function PaymentAnalytics() {
  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Payment Analytics</h1>
          <p className="page-subtitle">Advanced payment analytics and insights</p>
        </div>
        <Link to="/admin/payments" className="btn btn-outline btn-sm">← Back to Payments</Link>
      </div>
      <div className="card">
        <p>Payment analytics implementation would go here...</p>
      </div>
    </div>
  );
}
function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState('');
  const [selectedLogs, setSelectedLogs] = useState(new Set());
  const [archivedIds, setArchivedIds] = useState(new Set());
  const [view, setView] = useState('active'); // 'active' or 'archived'
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    loadLogs(currentPage);
  }, [currentPage, filterAction]);

  const loadLogs = async (page = 1) => {
    try {
      setLoading(true);
      const response = await auditAPI.getLogs({
        is_archived: 'all',
        page,
        limit: 50,
        action: filterAction
      });

      const { logs: allLogs, pagination: pageInfo } = response.data;
      setLogs(allLogs);
      setPagination(pageInfo);

      // Initialize archived IDs based on the currently loaded page
      const initialArchived = new Set(allLogs.filter(l => l.is_archived).map(l => l.id));
      setArchivedIds(initialArchived);
    } catch (error) {
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id) => {
    try {
      await auditAPI.archive(id);
      setArchivedIds(prev => new Set(prev).add(id));
      toast.success('Log entry archived');
    } catch (e) {
      toast.error('Failed to archive log entry');
    }
  };

  const handleRestore = async (id) => {
    try {
      await auditAPI.restore(id);
      setArchivedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success('Log entry restored');
    } catch (e) {
      toast.error('Failed to restore log entry');
    }
  };

  const handleDelete = async (id) => {
    try {
      if (!window.confirm('Permanently delete this log entry?')) return;
      await auditAPI.delete(id);
      setLogs(prev => prev.filter(l => l.id !== id));
      toast.success('Log entry deleted permanently');
    } catch (e) {
      toast.error('Failed to delete log entry');
    }
  };

  const toggleSelectAll = () => {
    const currentLogs = filteredLogs;
    if (selectedLogs.size === currentLogs.length) {
      setSelectedLogs(new Set());
    } else {
      setSelectedLogs(new Set(currentLogs.map(l => l.id)));
    }
  };

  const toggleSelect = (id) => {
    setSelectedLogs(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleBulkAction = async (action) => {
    if (selectedLogs.size === 0) return;

    const ids = Array.from(selectedLogs);
    try {
      await auditAPI.bulkAction(ids, action);

      if (action === 'delete') {
        setLogs(prev => prev.filter(l => !ids.includes(l.id)));
      } else if (action === 'archive') {
        setArchivedIds(prev => {
          const next = new Set(prev);
          ids.forEach(id => next.add(id));
          return next;
        });
      } else if (action === 'restore') {
        setArchivedIds(prev => {
          const next = new Set(prev);
          ids.forEach(id => next.delete(id));
          return next;
        });
      }

      setSelectedLogs(new Set());
      toast.success(`Bulk action ${action} completed`);
    } catch (e) {
      toast.error('Bulk action failed');
    }
  };

  const getActionIcon = (action) => {
    const icons = {
      'login': 'lock',
      'logout': 'logout',
      'login_failed': 'x-circle',
      'login_2fa_required': 'shield',
      'create_recruiter': 'user-check',
      'update_recruiter': 'pencil',
      'delete_recruiter': 'trash',
      'bulk_upload': 'upload',
      'update_profile': 'user',
      'password_changed': 'lock',
      'unauthorized_access': 'alert'
    };
    const name = icons[action];
    return name ? <Icon name={name} style={{ fontSize: '18px', verticalAlign: '-0.15em' }} /> : <Icon name="info" style={{ fontSize: '18px', verticalAlign: '-0.15em' }} />;
  };

  const getActionColor = (action) => {
    const colors = {
      'login': '#10b981',
      'logout': '#64748b',
      'login_failed': '#ef4444',
      'login_2fa_required': '#f59e0b',
      'create_recruiter': '#3b82f6',
      'update_recruiter': '#f59e0b',
      'delete_recruiter': '#ef4444',
      'bulk_upload': '#8b5cf6',
      'update_profile': '#6366f1',
      'password_changed': '#6366f1',
      'unauthorized_access': '#ef4444'
    };
    return colors[action] || '#64748b';
  };

  const filteredLogs = logs.filter(log => {
    const matchesAction = filterAction === '' || log.action === filterAction;
    const matchesView = view === 'archived' ? archivedIds.has(log.id) : !archivedIds.has(log.id);
    return matchesAction && matchesView;
  });

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="clipboard" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Audit Logs...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching system activity and security logs</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 className="page-title">Audit Logs</h1>
          <p className="page-subtitle">System activity monitoring, security logs, and user action tracking</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => {
              toast.info('Advanced Search feature - would provide detailed filtering options for audit logs');
            }}
          >
            <Icon name="search" /> Advanced Search
          </button>
          <button
            className="btn btn-outline"
            onClick={() => {
              // Export audit logs
              const csvContent = [
                'Timestamp,User,Action,Resource,Details,IP Address',
                ...logs.map(log => `${log.created_at},${log.username},${log.action},${log.resource},${log.details},${log.ip_address}`)
              ].join('\n');

              const blob = new Blob([csvContent], { type: 'text/csv' });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
              link.click();
              URL.revokeObjectURL(url);

              toast.success('Audit logs exported successfully!');
            }}
          >
            <Icon name="download" /> Export Logs
          </button>
        </div>
      </div>

      {/* Audit Stats - Commented out as requested */}
      {/*
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Total Events</div>
          </div>
          <div className="stat-card-value">{logs.length}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Last 30 days
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">🔐 Login Events</div>
            <div className="stat-card-icon">🔐</div>
          </div>
          <div className="stat-card-value">{logs.filter(log => log.action === 'login').length}</div>
        </div>
        
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Security Events</div>
          </div>
          <div className="stat-card-value">{logs.filter(log => log.action.includes('unauthorized')).length}</div>
          <div style={{ fontSize: '13px', color: '#ef4444', fontWeight: '600', marginTop: '8px' }}>
            Requires attention
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-card-header">
            <div className="stat-card-title">Active Users</div>
            <div className="stat-card-icon">👥</div>
          </div>
          <div className="stat-card-value">{new Set(logs.map(log => log.user_id)).size}</div>
          <div style={{ fontSize: '13px', color: '#64748b', marginTop: '8px' }}>
            Unique users today
          </div>
        </div>
      </div>
      */}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px' }}>
        <button
          className={`btn ${view === 'active' ? 'btn-primary' : 'btn-outline'} btn-sm`}
          onClick={() => { setView('active'); setSelectedLogs(new Set()); }}
        >
          Active Logs ({logs.filter(l => !archivedIds.has(l.id)).length})
        </button>
        <button
          className={`btn ${view === 'archived' ? 'btn-primary' : 'btn-outline'} btn-sm`}
          onClick={() => { setView('archived'); setSelectedLogs(new Set()); }}
        >
          Archive ({archivedIds.size})
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}><Icon name="target" /> Filter by Action</label>
            <select
              className="form-select"
              value={filterAction}
              onChange={(e) => {
                setFilterAction(e.target.value);
                setCurrentPage(1);
              }}
              style={{ minWidth: '200px' }}
            >
              <option value="">All Actions</option>
              {Array.from(new Set(logs.map(l => l.action))).map(action => (
                <option key={action} value={action}>
                  {getActionIcon(action)} {action.replace(/_/g, ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', display: 'block' }}>⏰ Quick Jump</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="btn btn-outline btn-sm" onClick={() => toast.info('Filtering last 24h...')}>24h</button>
              <button className="btn btn-outline btn-sm" onClick={() => toast.info('Filtering last 7d...')}>7d</button>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Action Bar */}
      {selectedLogs.size > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px',
          backgroundColor: '#f8fafc',
          borderRadius: '12px',
          marginBottom: '24px',
          border: '1px solid #e2e8f0',
          animation: 'slideIn 0.3s ease-out'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="checkbox"
              checked={selectedLogs.size === filteredLogs.length && filteredLogs.length > 0}
              onChange={toggleSelectAll}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span style={{ fontWeight: '600', color: '#475569' }}>
              {selectedLogs.size} selected
            </span>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {view === 'archived' ? (
              <button className="btn btn-primary btn-sm" onClick={() => handleBulkAction('restore')}>Restore</button>
            ) : (
              <button className="btn btn-secondary btn-sm" onClick={() => handleBulkAction('archive')}>Archive</button>
            )}
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">System Activity ({filteredLogs.length})</h2>
            <p className="card-subtitle">Detailed log of all user actions and system events</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                setLoading(true);
                loadLogs();
                toast.success('Audit logs refreshed!');
              }}
            >
              <Icon name="refresh" /> Refresh
            </button>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => {
                toast.info('Audit settings - would configure log retention, alert thresholds, and monitoring preferences');
              }}
            >
              <Icon name="settings" /> Settings
            </button>
          </div>
        </div>
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={selectedLogs.size === filteredLogs.length && filteredLogs.length > 0}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th><Icon name="user" /> User</th>
                <th><Icon name="target" /> Action</th>
                <th><Icon name="folder-open" /> Resource</th>
                <th><Icon name="pencil" /> Details</th>
                <th><Icon name="globe" /> IP/Time</th>
                <th style={{ textAlign: 'right' }}><Icon name="settings" /> Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.map((log, index) => (
                <tr key={log.id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedLogs.has(log.id)}
                      onChange={() => toggleSelect(log.id)}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${index % 2 === 0 ? '#3b82f6' : '#10b981'}, ${index % 2 === 0 ? '#8b5cf6' : '#f59e0b'})`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '12px'
                      }}>
                        {log.username ? log.username.substring(0, 2).toUpperCase() : '??'}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>{log.username || 'System'}</div>
                        <div style={{ fontSize: '12px', color: '#64748b' }}>{log.email || 'Automated'}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '18px' }}>{getActionIcon(log.action)}</span>
                      <span
                        className="badge badge-info"
                        style={{
                          backgroundColor: `${getActionColor(log.action)}20`,
                          color: getActionColor(log.action),
                          border: `1px solid ${getActionColor(log.action)}40`
                        }}
                      >
                        {log.action.replace(/_/g, ' ').toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td>
                    <div>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>{log.resource || 'System'}</div>
                      <div style={{ fontSize: '12px', color: '#64748b' }}>
                        {log.resource_id ? `ID: ${log.resource_id}` : 'Global'}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div style={{
                      maxWidth: '250px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontSize: '13px',
                      lineHeight: '1.4'
                    }}>
                      {log.details}
                    </div>
                  </td>
                  <td>
                    <div style={{ fontSize: '13px' }}>
                      <div style={{ fontWeight: '600', color: '#475569' }}>{log.ip_address}</div>
                      <div style={{ color: '#64748b', fontSize: '12px' }}>{new Date(log.created_at).toLocaleString()}</div>
                    </div>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', opacity: 0.7 }}>
                      {archivedIds.has(log.id) ? (
                        <button className="btn-icon" onClick={() => handleRestore(log.id)} title="Restore"><Icon name="corner-up-left" /></button>
                      ) : (
                        <button className="btn-icon" onClick={() => handleArchive(log.id)} title="Archive"><Icon name="download" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        <div style={{
          padding: '24px 32px',
          borderTop: '1px solid #f1f5f9',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#fff',
          borderRadius: '0 0 16px 16px'
        }}>
          <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '500' }}>
            Showing <span style={{ color: '#64748b', fontWeight: '500' }}>{(((pagination?.page || 1) - 1) * (pagination?.limit || 50)) + 1}</span> to{' '}
            <span style={{ color: '#1e293b', fontWeight: '600' }}>{Math.min((pagination?.page || 1) * (pagination?.limit || 50), pagination?.total || 0)}</span> of{' '}
            <span style={{ color: '#1e293b', fontWeight: '600' }}>{pagination?.total || 0}</span> records
          </div>

          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              className={`btn btn-outline btn-sm`}
              onClick={() => {
                const newPage = Math.max(1, currentPage - 1);
                setCurrentPage(newPage);
              }}
              disabled={currentPage === 1 || loading}
              style={{ padding: '6px 12px' }}
            >
              <Icon name="chevron-left" /> Previous
            </button>

            <div style={{ display: 'flex', gap: '4px' }}>
              {Array.from({ length: Math.min(5, Math.ceil((pagination?.total || 0) / (pagination?.limit || 50))) }, (_, i) => {
                const totalPages = Math.ceil((pagination?.total || 0) / (pagination?.limit || 50));
                let pageNum;

                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    className={`btn ${currentPage === pageNum ? 'btn-primary' : 'btn-outline'} btn-xs`}
                    onClick={() => setCurrentPage(pageNum)}
                    style={{ minWidth: '32px', height: '32px', padding: 0 }}
                    disabled={loading}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              className={`btn btn-outline btn-sm`}
              onClick={() => {
                const newPage = Math.min(Math.ceil((pagination?.total || 0) / (pagination?.limit || 50)), currentPage + 1);
                setCurrentPage(newPage);
              }}
              disabled={currentPage >= Math.ceil((pagination?.total || 0) / (pagination?.limit || 50)) || loading}
              style={{ padding: '6px 12px' }}
            >
              Next <Icon name="chevron-right" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  const [archivedIds, setArchivedIds] = useState(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 20, page: 1 });

  useEffect(() => {
    loadNotifications();
  }, [filter, currentPage]);

  const loadNotifications = async () => {
    try {
      const params = { admin: true, is_archived: filter === 'archived' ? 'true' : 'false', page: currentPage, limit: pagination.limit };
      if (filter !== 'all' && filter !== 'archived') {
        if (filter === 'unread') {
          params.is_read = false;
        } else {
          params.type = filter;
        }
      }

      const response = await notificationAPI.getAll(params);
      setNotifications(response.data.notifications || []);
      setPagination(response.data.pagination || { total: 0, limit: 10, page: 1 });

      const initialArchived = new Set((response.data.notifications || []).filter(n => n.is_archived).map(n => n.id));
      setArchivedIds(initialArchived);

      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationAPI.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
      toast.success('Notification marked as read');
    } catch (error) {
      toast.error('Failed to mark notification as read');
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead(true); // true for admin
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all notifications as read');
    }
  };

  const handleArchive = async (id) => {
    try {
      await notificationAPI.archive(id);
      setArchivedIds(prev => new Set(prev).add(id));
      toast.success('Notification archived');
    } catch (e) {
      toast.error('Failed to archive notification');
    }
  };

  const handleRestore = async (id) => {
    try {
      await notificationAPI.restore(id);
      setArchivedIds(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success('Notification restored');
    } catch (e) {
      toast.error('Failed to restore notification');
    }
  };

  const handleDelete = async (id) => {
    try {
      if (!window.confirm('Permanently delete this notification?')) return;
      await notificationAPI.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      toast.success('Notification deleted permanently');
    } catch (e) {
      toast.error('Failed to delete notification');
    }
  };

  const handleBulkAction = async (action) => {
    if (selectedNotifications.size === 0) return;
    if (action === 'delete' && !window.confirm(`Permanently delete ${selectedNotifications.size} notifications?`)) return;

    const ids = Array.from(selectedNotifications);
    try {
      await notificationAPI.bulkAction(ids, action, true);

      if (action === 'read') {
        setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, is_read: true } : n));
      } else if (action === 'delete') {
        setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
      } else if (action === 'archive') {
        const newArchived = new Set(archivedIds);
        ids.forEach(id => newArchived.add(id));
        setArchivedIds(newArchived);
      } else if (action === 'restore') {
        const newArchived = new Set(archivedIds);
        ids.forEach(id => newArchived.delete(id));
        setArchivedIds(newArchived);
      }

      setSelectedNotifications(new Set());
      toast.success(`Successfully ${action === 'read' ? 'marked as read' : action + 'd'} selected notifications`);
    } catch (e) {
      toast.error(`Failed to ${action} notifications`);
    }
  };

  const activeNotifications = notifications.filter(n => !archivedIds.has(n.id));
  const archivedNotificationsList = notifications.filter(n => archivedIds.has(n.id));
  const currentUnreadCount = activeNotifications.filter(n => !n.is_read).length;

  const displayNotifications = filter === 'archived'
    ? archivedNotificationsList
    : activeNotifications.filter(n => {
      if (filter === 'all') return true;
      if (filter === 'unread') return !n.is_read;
      if (filter === 'recruiter_registration') return n.type === 'recruiter_registration';
      if (filter === 'payment_processed') return ['payment_processed', 'payment_pending'].includes(n.type);
      if (filter === 'system_alert') return ['system_alert', 'audit_alert'].includes(n.type);
      if (filter === 'performance_milestone') return ['performance_milestone', 'recruiter_performance'].includes(n.type);
      if (filter === 'system_maintenance') return ['system_maintenance', 'system_update'].includes(n.type);
      return false;
    });

  const formatTimeAgo = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInHours = Math.floor((now - date) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;

    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks} week${diffInWeeks > 1 ? 's' : ''} ago`;

    const diffInMonths = Math.floor(diffInDays / 30);
    return `${diffInMonths} month${diffInMonths > 1 ? 's' : ''} ago`;
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'recruiter_registration': return '';
      case 'payment_processed': return '';
      case 'system_alert': return '';
      case 'bulk_upload': return '';
      case 'performance_milestone': return '';
      case 'recruiter_performance': return '';
      case 'system_maintenance': return '';
      case 'payment_pending': return '';
      case 'audit_alert': return '';
      case 'system_update': return '';
      default: return '';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'recruiter_registration': return '#3b82f6';
      case 'payment_processed': return '#10b981';
      case 'system_alert': return '#ef4444';
      case 'bulk_upload': return '#8b5cf6';
      case 'performance_milestone': return '#f59e0b';
      case 'recruiter_performance': return '#ec4899';
      case 'system_maintenance': return '#64748b';
      case 'payment_pending': return '#f59e0b';
      case 'audit_alert': return '#ef4444';
      case 'system_update': return '#037b90';
      default: return '#64748b';
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="bell" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Notifications...</h3>
          <p style={{ color: '#94a3b8', marginTop: '8px' }}>Fetching system activities and alerts</p>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">System Notifications</h1>
          <p className="page-subtitle">Monitor system activities, alerts, and important administrative updates</p>
        </div>
        <div style={{ display: 'flex', gap: '16px' }}>
          {currentUnreadCount > 0 && (
            <button
              className="btn btn-outline btn-sm"
              onClick={handleMarkAllAsRead}
            >
              Mark All Read ({currentUnreadCount})
            </button>
          )}
          <button className="btn btn-secondary btn-sm">Settings</button>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="card">
        <div style={{ display: 'flex', gap: '16px', borderBottom: '2px solid #f1f5f9', paddingBottom: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'All', count: activeNotifications.length },
            { key: 'unread', label: 'Unread', count: currentUnreadCount },
            { key: 'archived', label: 'Archived', count: archivedNotificationsList.length },
            { key: 'recruiter_registration', label: 'Recruiters', count: activeNotifications.filter(n => n.type === 'recruiter_registration').length },
            { key: 'payment_processed', label: 'Payments', count: activeNotifications.filter(n => ['payment_processed', 'payment_pending'].includes(n.type)).length },
            { key: 'system_alert', label: 'Alerts', count: activeNotifications.filter(n => ['system_alert', 'audit_alert'].includes(n.type)).length },
            { key: 'performance_milestone', label: 'Performance', count: activeNotifications.filter(n => ['performance_milestone', 'recruiter_performance'].includes(n.type)).length },
            { key: 'system_maintenance', label: 'System', count: activeNotifications.filter(n => ['system_maintenance', 'system_update'].includes(n.type)).length }
          ].map(tab => (
            <button
              key={tab.key}
              className={`btn ${filter === tab.key ? 'btn-primary' : 'btn-outline'} btn-sm`}
              onClick={() => setFilter(tab.key)}
              style={{ minWidth: 'auto' }}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>

        {/* Bulk Actions Menu */}
        {selectedNotifications.size > 0 && (
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', padding: '12px', background: '#f8fafc', borderRadius: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#037b90', marginRight: '8px' }}>
              {selectedNotifications.size} selected
            </span>
            {filter !== 'archived' && (
              <>
                <button className="btn btn-outline btn-sm" onClick={() => handleBulkAction('read')}>
                  <Icon name="check" /> Mark Read
                </button>
                <button className="btn btn-outline btn-sm" onClick={() => handleBulkAction('archive')}>
                  <Icon name="download" /> Archive
                </button>
              </>
            )}
            {filter === 'archived' && (
              <button className="btn btn-outline btn-sm" onClick={() => handleBulkAction('restore')}>
                <Icon name="refresh" /> Restore
              </button>
            )}
            <button className="btn btn-danger btn-sm" onClick={() => handleBulkAction('delete')}>
              <Icon name="trash" /> Delete
            </button>
          </div>
        )}

        {/* Notifications List */}
        <div className="notifications-list">
          {displayNotifications.length > 0 && (
            <div style={{ padding: '0 16px 12px', borderBottom: '1px solid #f1f5f9', marginBottom: '12px', marginLeft: '12px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: '#64748b' }}>
                <input
                  type="checkbox"
                  style={{ width: '16px', height: '16px' }}
                  checked={displayNotifications.length > 0 && selectedNotifications.size === displayNotifications.length}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedNotifications(new Set(displayNotifications.map(n => n.id)));
                    else setSelectedNotifications(new Set());
                  }}
                />
                Select All
              </label>
            </div>
          )}

          {displayNotifications.length > 0 ? (
            displayNotifications.map((notification) => (
              <div
                key={notification.id}
                className={`notification-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={(e) => {
                  // Do not mark as read if clicking within action buttons/checkboxes
                  if (e.target.tagName !== 'INPUT' && e.target.tagName !== 'BUTTON') {
                    if (!notification.is_read) handleMarkAsRead(notification.id);
                  }
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', marginRight: '16px' }}>
                  <input
                    type="checkbox"
                    style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                    checked={selectedNotifications.has(notification.id)}
                    onChange={(e) => {
                      const newSelected = new Set(selectedNotifications);
                      if (e.target.checked) newSelected.add(notification.id);
                      else newSelected.delete(notification.id);
                      setSelectedNotifications(newSelected);
                    }}
                  />
                </div>
                <div className="notification-icon" style={{ color: getNotificationColor(notification.type) }}>
                  {getNotificationIcon(notification.type)}
                </div>

                <div className="notification-content">
                  <div className="notification-header">
                    <h4 className="notification-title">{notification.title}</h4>
                    <div className="notification-meta">
                      <span className="notification-time">{formatTimeAgo(notification.created_at)}</span>
                      {!notification.is_read && <span className="notification-unread-dot"></span>}
                    </div>
                  </div>

                  <p className="notification-message">{notification.message}</p>

                  {notification.data && (
                    <div className="notification-data">
                      {notification.type === 'recruiter_registration' && notification.data.recruiter_name && (
                        <div className="notification-detail">
                          <span className="badge badge-info">Recruiter: {notification.data.recruiter_name}</span>
                          {notification.data.referral_code && (
                            <span className="badge badge-success">Code: {notification.data.referral_code}</span>
                          )}
                        </div>
                      )}

                      {(notification.type === 'payment_processed' || notification.type === 'payment_pending') && notification.data.total_amount && (
                        <div className="notification-detail">
                          <span className="badge badge-success">Amount: KES {notification.data.total_amount.toLocaleString()}</span>
                          {notification.data.recruiter_count && (
                            <span className="badge badge-info">{notification.data.recruiter_count} recruiters</span>
                          )}
                        </div>
                      )}

                      {notification.type === 'system_alert' && notification.data.admission_count && (
                        <div className="notification-detail">
                          <span className="badge badge-warning">Count: {notification.data.admission_count}</span>
                          {notification.data.percentage_increase && (
                            <span className="badge badge-success">+{notification.data.percentage_increase}%</span>
                          )}
                        </div>
                      )}

                      {notification.type === 'bulk_upload' && (
                        <div className="notification-detail">
                          {notification.data.success_count && (
                            <span className="badge badge-success">Success: {notification.data.success_count}</span>
                          )}
                          {notification.data.error_count && (
                            <span className="badge badge-danger">Errors: {notification.data.error_count}</span>
                          )}
                        </div>
                      )}

                      {(notification.type === 'performance_milestone' || notification.type === 'recruiter_performance') && notification.data.milestone && (
                        <div className="notification-detail">
                          <span className="badge badge-warning">Milestone: {notification.data.milestone}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginLeft: '16px' }}>
                  {filter !== 'archived' ? (
                    <>
                      {!notification.is_read && (
                        <button
                          className="btn btn-outline btn-sm notification-mark-read"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification.id);
                          }}
                          title="Mark as read"
                          style={{ padding: '4px 8px' }}
                        >
                          <Icon name="check" />
                        </button>
                      )}
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={(e) => { e.stopPropagation(); handleArchive(notification.id); }}
                        title="Archive"
                        style={{ padding: '4px 8px' }}
                      >
                        <Icon name="download" />
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-outline btn-sm"
                      onClick={(e) => { e.stopPropagation(); handleRestore(notification.id); }}
                      title="Restore"
                      style={{ padding: '4px 8px', color: '#037b90', borderColor: '#cffafe' }}
                    >
                      <Icon name="refresh" />
                    </button>
                  )}
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={(e) => { e.stopPropagation(); handleDelete(notification.id); }}
                    title="Delete Permanently"
                    style={{ padding: '4px 8px', color: '#ef4444', borderColor: '#fee2e2' }}
                  >
                    <Icon name="trash" />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 0', color: '#64748b' }}>
              <div style={{ fontSize: '64px', marginBottom: '24px', opacity: 0.5 }}><Icon name="bell" /></div>
              <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Notifications</h3>
              <p style={{ fontSize: '14px', marginBottom: '24px' }}>
                {filter === 'all'
                  ? "No system notifications yet. Administrative activities will appear here."
                  : `No ${filter} notifications found. Try selecting a different filter.`
                }
              </p>
              {filter !== 'all' && (
                <button
                  className="btn btn-outline"
                  onClick={() => setFilter('all')}
                >
                  View All Notifications
                </button>
              )}
            </div>
          )}
        </div>
        <Pagination
          pagination={pagination}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loading={loading}
        />
      </div>
    </div>
  );
}

// Student Management Modal Component
function StudentManagementModal({ onClose }) {
  const [activeTab, setActiveTab] = useState('admission');
  const [recruiters, setRecruiters] = useState([]);
  const [admittedStudents, setAdmittedStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [formData, setFormData] = useState({
    student_id: '',
    student_name: '',
    student_phone: '',
    programme: '',
    recruiter_id: '',
    admission_date: new Date().toISOString().split('T')[0],
    enrollment_date: new Date().toISOString().split('T')[0]
  });

  // Sample programmes for auto-generation
  const programmes = [
    'Bachelor of Business Administration',
    'Bachelor of Computer Science',
    'Bachelor of Education',
    'Bachelor of Arts in Economics',
    'Bachelor of Science in Mathematics',
    'Master of Business Administration',
    'Master of Computer Science',
    'Master of Education',
    'Diploma in Business Management',
    'Diploma in Information Technology',
    'Certificate in Project Management',
    'Certificate in Digital Marketing'
  ];

  // Sample Kenyan names for auto-generation
  const sampleNames = [
    'John Mwangi Kamau', 'Mary Wanjiku Njeri', 'Peter Ochieng Otieno', 'Grace Akinyi Ouma',
    'David Kiprop Koech', 'Sarah Nyambura Maina', 'Michael Wekesa Barasa', 'Faith Chebet Rono',
    'James Karanja Githui', 'Lucy Wanjiru Ndung\'u', 'Samuel Kiprotich Bett', 'Rose Njambi Kuria',
    'Daniel Omondi Owino', 'Catherine Wairimu Gitau', 'Joseph Mutua Musyoka', 'Agnes Moraa Nyong\'o'
  ];

  useEffect(() => {
    loadRecruiters();
    if (activeTab === 'enrollment') {
      loadAdmittedStudents();
    }
  }, [activeTab]);

  const loadRecruiters = async () => {
    try {
      const response = await recruiterAPI.getAll();
      const recruitersData = response.data.recruiters || (Array.isArray(response.data) ? response.data : []);
      setRecruiters(recruitersData);
    } catch (error) {
      toast.error('Failed to load recruiters');
    }
  };

  const loadAdmittedStudents = async () => {
    try {
      const response = await studentAPI.getAdmittedStudents();
      setAdmittedStudents(response.data.admittedStudents);
    } catch (error) {
      toast.error('Failed to load admitted students');
    }
  };

  const handleStudentSelect = (e) => {
    const selectedStudentId = e.target.value;
    if (selectedStudentId) {
      const student = admittedStudents.find(s => s.student_id === selectedStudentId);
      if (student) {
        setSelectedStudent(student);
        setFormData({
          ...formData,
          student_id: selectedStudentId,
          recruiter_id: student.recruiter_id.toString(),
          enrollment_date: new Date().toISOString().split('T')[0]
        });
      }
    } else {
      setSelectedStudent(null);
      setFormData({
        ...formData,
        student_id: '',
        recruiter_id: '',
        enrollment_date: new Date().toISOString().split('T')[0]
      });
    }
  };

  const generateStudentData = () => {
    const randomName = sampleNames[Math.floor(Math.random() * sampleNames.length)];
    const randomProgramme = programmes[Math.floor(Math.random() * programmes.length)];
    const randomPhone = `+2547${Math.floor(Math.random() * 90000000) + 10000000}`;
    const studentId = generateStudentId();

    setFormData({
      ...formData,
      student_id: studentId,
      student_name: randomName,
      student_phone: randomPhone,
      programme: randomProgramme
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (activeTab === 'admission') {
      if (!formData.student_id || !formData.student_name || !formData.programme || !formData.recruiter_id) {
        toast.error('Please fill in all required fields');
        return;
      }
    } else {
      if (!selectedStudent) {
        toast.error('Please select an admitted student');
        return;
      }
    }

    setLoading(true);

    try {
      const selectedRecruiter = recruiters.find(r => r.id === parseInt(formData.recruiter_id));

      if (activeTab === 'admission') {
        await studentAPI.addAdmission({
          student_id: formData.student_id,
          student_name: formData.student_name,
          student_phone: formData.student_phone,
          programme: formData.programme,
          recruiter_id: parseInt(formData.recruiter_id),
          admission_date: formData.admission_date
        });

        toast.success(` Student admission added successfully! ${selectedRecruiter?.full_name} earned KES 500 commission.`);
      } else {
        await studentAPI.addEnrollment({
          student_id: formData.student_id,
          recruiter_id: parseInt(formData.recruiter_id),
          enrollment_date: formData.enrollment_date
        });

        toast.success(` Student enrollment added successfully! ${selectedRecruiter?.full_name} earned KES 300 commission.`);

        // Refresh admitted students list
        await loadAdmittedStudents();
        setSelectedStudent(null);
      }

      // Reset form
      setFormData({
        student_id: '',
        student_name: '',
        student_phone: '',
        programme: '',
        recruiter_id: '',
        admission_date: new Date().toISOString().split('T')[0],
        enrollment_date: new Date().toISOString().split('T')[0]
      });

    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      toast.error(`Failed to add student ${activeTab}: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };

  const generateStudentId = () => {
    const prefix = activeTab === 'admission' ? 'ADM' : 'ENR';
    const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const year = new Date().getFullYear();
    return `${prefix}${year}${randomNum}`;
  };

  const availableStudentsForEnrollment = admittedStudents.filter(s => !s.already_enrolled);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
        <div className="modal-header">
          <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Icon name="graduation-cap" /> Simulate Student API Data
          </h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p style={{ color: '#64748b', marginBottom: '24px', fontSize: '14px' }}>
            Simulate receiving student data from external API. This represents data that would normally come from the university's student management system.
            {activeTab === 'enrollment' && (
              <span style={{ color: '#dc2626', fontWeight: '600' }}>
                <br />Note: Only students who have been admitted can be enrolled for sessions.
              </span>
            )}
          </p>

          {/* Tab Navigation */}
          <div style={{ display: 'flex', borderBottom: '2px solid #f1f5f9', marginBottom: '24px' }}>
            <button
              className={`tab-button ${activeTab === 'admission' ? 'active' : ''}`}
              onClick={() => setActiveTab('admission')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'admission' ? '2px solid #3b82f6' : '2px solid transparent',
                color: activeTab === 'admission' ? '#3b82f6' : '#64748b',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <Icon name="graduation-cap" /> Student Admission
            </button>
            <button
              className={`tab-button ${activeTab === 'enrollment' ? 'active' : ''}`}
              onClick={() => setActiveTab('enrollment')}
              style={{
                padding: '12px 24px',
                border: 'none',
                background: 'none',
                borderBottom: activeTab === 'enrollment' ? '2px solid #10b981' : '2px solid transparent',
                color: activeTab === 'enrollment' ? '#10b981' : '#64748b',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              <Icon name="book" /> Session Enrollment
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gap: '16px' }}>
              {activeTab === 'admission' ? (
                // Admission Form Fields
                <>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f0f9ff',
                    borderRadius: '8px',
                    border: '1px solid #dbeafe',
                    marginBottom: '8px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <h4 style={{ margin: '0 0 4px 0', color: '#1e40af', fontSize: '14px', fontWeight: '600' }}>
                          <Icon name="dice" /> Auto-Generate Student Data
                        </h4>
                        <p style={{ margin: 0, fontSize: '12px', color: '#1e40af' }}>
                          Generate realistic student information for testing
                        </p>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={generateStudentData}
                      >
                        <Icon name="dice" /> Generate All
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Student ID *
                    </label>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <input
                        type="text"
                        className="form-input"
                        value={formData.student_id}
                        onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                        placeholder="e.g., ADM202400123"
                        required
                        style={{ flex: 1 }}
                      />
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        onClick={() => setFormData({ ...formData, student_id: generateStudentId() })}
                      >
                        <Icon name="dice" /> ID
                      </button>
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Student Name *
                    </label>
                    <input
                      type="text"
                      className="form-input"
                      value={formData.student_name}
                      onChange={(e) => setFormData({ ...formData, student_name: e.target.value })}
                      placeholder="e.g., John Doe Mwangi"
                      required
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Student Phone
                    </label>
                    <input
                      type="tel"
                      className="form-input"
                      value={formData.student_phone}
                      onChange={(e) => setFormData({ ...formData, student_phone: e.target.value })}
                      placeholder="e.g., +254712345678"
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Programme *
                    </label>
                    <select
                      className="form-select"
                      value={formData.programme}
                      onChange={(e) => setFormData({ ...formData, programme: e.target.value })}
                      required
                    >
                      <option value="">Select a programme...</option>
                      {programmes.map(programme => (
                        <option key={programme} value={programme}>
                          {programme}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Recruiter *
                    </label>
                    <select
                      className="form-select"
                      value={formData.recruiter_id}
                      onChange={(e) => setFormData({ ...formData, recruiter_id: e.target.value })}
                      required
                    >
                      <option value="">Select a recruiter...</option>
                      {recruiters.map(recruiter => (
                        <option key={recruiter.id} value={recruiter.id}>
                          {recruiter.full_name} ({recruiter.referral_code})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Admission Date
                    </label>
                    <input
                      type="date"
                      className="form-input"
                      value={formData.admission_date}
                      onChange={(e) => setFormData({ ...formData, admission_date: e.target.value })}
                    />
                  </div>
                </>
              ) : (
                // Enrollment Form Fields - Simplified
                <>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                      Select Admitted Student *
                    </label>
                    <select
                      className="form-select"
                      value={formData.student_id}
                      onChange={handleStudentSelect}
                      required
                    >
                      <option value="">Select an admitted student...</option>
                      {availableStudentsForEnrollment.map(student => (
                        <option key={`${student.student_id}-${student.recruiter_id}`} value={student.student_id}>
                          {student.student_id} - {student.student_name} ({student.programme})
                        </option>
                      ))}
                    </select>
                    {availableStudentsForEnrollment.length === 0 && (
                      <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '4px' }}>
                        No admitted students available for enrollment. Please add admissions first.
                      </p>
                    )}
                  </div>

                  {selectedStudent && (
                    <>
                      <div style={{
                        padding: '16px',
                        backgroundColor: '#f0fdf4',
                        borderRadius: '8px',
                        border: '1px solid #dcfce7'
                      }}>
                        <h4 style={{ margin: '0 0 12px 0', color: '#166534', fontSize: '14px', fontWeight: '600' }}>
                          <Icon name="clipboard" /> Student Details (Auto-populated)
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                          <div>
                            <strong>Name:</strong> {selectedStudent.student_name}
                          </div>
                          <div>
                            <strong>Phone:</strong> {selectedStudent.student_phone || 'Not provided'}
                          </div>
                          <div>
                            <strong>Programme:</strong> {selectedStudent.programme}
                          </div>
                          <div>
                            <strong>Recruited by:</strong> {selectedStudent.recruiter_name}
                          </div>
                          <div>
                            <strong>Admission Date:</strong> {new Date(selectedStudent.admission_date).toLocaleDateString()}
                          </div>
                          <div>
                            <strong>Referral Code:</strong> {selectedStudent.referral_code}
                          </div>
                        </div>
                      </div>

                      <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600' }}>
                          Enrollment Date
                        </label>
                        <input
                          type="date"
                          className="form-input"
                          value={formData.enrollment_date}
                          onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
                          min={selectedStudent.admission_date}
                        />
                        <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                          Must be after admission date ({new Date(selectedStudent.admission_date).toLocaleDateString()})
                        </p>
                      </div>
                    </>
                  )}
                </>
              )}

              <div style={{
                padding: '16px',
                backgroundColor: activeTab === 'admission' ? '#eff6ff' : '#f0fdf4',
                borderRadius: '8px',
                border: `1px solid ${activeTab === 'admission' ? '#dbeafe' : '#dcfce7'}`
              }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  color: activeTab === 'admission' ? '#1e40af' : '#166534',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  {activeTab === 'admission' ? <><Icon name="graduation-cap" />{" Admission Commission"}</> : <><Icon name="book" />{" Enrollment Commission"}</>}
                </h4>
                <p style={{
                  margin: 0,
                  fontSize: '13px',
                  color: activeTab === 'admission' ? '#1e40af' : '#166534'
                }}>
                  {activeTab === 'admission'
                    ? 'KES 500 will be credited to the recruiter for this admission'
                    : 'KES 300 will be credited to the recruiter for this enrollment'
                  }
                </p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button
                type="submit"
                className={`btn ${activeTab === 'admission' ? 'btn-primary' : 'btn-success'} ${loading ? 'loading' : ''}`}
                disabled={loading || (activeTab === 'enrollment' && !selectedStudent)}
              >
                {loading ? <><Icon name="clock" />{" Processing..."}</> :
                  activeTab === 'admission' ? <><Icon name="graduation-cap" />{" Add Admission"}</> :
                    <><Icon name="book" />{" Enroll Student"}</>}
              </button>
              <button
                type="button"
                className="btn btn-outline"
                onClick={onClose}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// User Management Component
function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, page: 1 });

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    role: '',
    fullName: ''
  });

  useEffect(() => {
    loadUsers();
    loadRoles();
  }, [currentPage]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getAll(currentPage, pagination.limit, 'recruiter');
      setUsers(response.data.users || []);
      setPagination(response.data.pagination || { total: 0, limit: 50, page: 1 });
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const response = await roleAPI.getAll();
      // Filter out 'recruiter' role from user management
      const filteredRoles = response.data.filter(r => r.name !== 'recruiter');
      setRoles(filteredRoles);

      // Set default role to the first available role if no role is currently selected
      if (filteredRoles.length > 0 && !formData.role) {
        setFormData(prev => ({ ...prev, role: filteredRoles[0].name }));
      }
    } catch (error) {
      toast.error('Failed to load roles');
      console.error('Error loading roles:', error);
      // Fallback to basic roles if API fails (excluding recruiter)
      setRoles([
        { id: 1, name: 'admin', display_name: 'Administrator' },
        { id: 2, name: 'financial_officer', display_name: 'Financial Officer' }
      ]);
    }
  };

  // Function to get role display information
  const getRoleDisplay = (roleName) => {
    const role = roles.find(r => r.name === roleName);
    if (role) {
      const getEmoji = (name) => {
        switch (name) {
          case 'admin': return '';
          case 'financial_officer': return '';
          case 'recruiter': return '';
          default: return '';
        }
      };

      const getBadgeClass = (name) => {
        switch (name) {
          case 'admin': return 'badge-danger';
          case 'financial_officer': return 'badge-info';
          case 'recruiter': return 'badge-success';
          default: return 'badge-secondary';
        }
      };

      return {
        emoji: getEmoji(roleName),
        displayName: role.display_name || role.name,
        badgeClass: getBadgeClass(roleName)
      };
    }

    // Fallback for unknown roles
    return {
      emoji: '',
      displayName: roleName,
      badgeClass: 'badge-secondary'
    };
  };


  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Automatically set username to email
      const submitData = {
        ...formData,
        username: formData.email
      };

      if (editingUser) {
        await userAPI.update(editingUser.id, submitData);
        toast.success('User updated successfully');
      } else {
        const response = await userAPI.create(submitData);
        if (response.data.emailSent) {
          toast.success('User created and credentials sent via email');
        } else {
          toast.warning('User created but email failed to send');
        }
      }

      setShowAddModal(false);
      setEditingUser(null);
      setFormData({ email: '', role: roles.length > 0 ? roles.filter(r => r.name !== 'recruiter')[0]?.name || '' : '', fullName: '' });
      loadUsers();
    } catch (error) {
      console.error('User creation/update error:', error);

      // Handle different types of errors
      if (error.message.includes('Too many requests')) {
        toast.error('Rate limit exceeded. Please wait a moment and try again.');
      } else if (error.message.includes('already exists')) {
        toast.error('Username or email already exists');
      } else {
        toast.error(error.message || 'Failed to save user');
      }
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.email, // Set username to email for consistency
      email: user.email,
      role: user.role,
      fullName: user.full_name || ''
    });
    setShowAddModal(true);
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.username}"?`)) {
      try {
        await userAPI.delete(user.id);
        toast.success('User deleted successfully');
        loadUsers();
      } catch (error) {
        toast.error(error.message || 'Failed to delete user');
      }
    }
  };

  const toggleUserStatus = async (user) => {
    try {
      await userAPI.update(user.id, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? 'deactivated' : 'activated'} successfully`);
      loadUsers();
    } catch (error) {
      toast.error(error.message || 'Failed to update user status');
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div className="stat-card-icon loading" style={{ width: '64px', height: '64px', fontSize: '28px', margin: '0 auto 24px' }}>
            <Icon name="users" />
          </div>
          <h3 style={{ color: '#64748b', fontWeight: '600' }}>Loading Users...</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <PageHeader
        title="User Management"
        subtitle="Manage admin and finance officer accounts"
        icon="user"
        actions={
          <button
            className="btn btn-primary"
            onClick={() => {
              setEditingUser(null);
              setFormData({ email: '', role: roles.length > 0 ? roles[0].name : '', fullName: '' });
              setShowAddModal(true);
            }}
          >
            <Icon name="user" /> Add New User
          </button>
        }
       />

      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="card-title">System Users</h2>
            <p className="card-subtitle">Admin and finance officer accounts</p>
          </div>
        </div>

        {users.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}><Icon name="users" /></div>
            <h3 style={{ color: '#64748b', fontWeight: '600', marginBottom: '8px' }}>No Users Found</h3>
            <p style={{ color: '#94a3b8', marginBottom: '24px' }}>
              Create your first admin or finance officer account
            </p>
            <button
              className="btn btn-primary"
              onClick={() => setShowAddModal(true)}
            >
              <Icon name="user" /> Add First User
            </button>
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user, index) => (
                    <tr key={user.id}>
                      <td>
                        <div style={{ fontWeight: '600', fontSize: '15px' }}>{user.username || 'Unknown'}</div>
                        <div style={{ fontSize: '13px', color: '#64748b' }}>{user.email}</div>
                      </td>
                      <td>
                        <span style={{ fontSize: '13px', color: '#64748b' }}>{getRoleDisplay(user.role).displayName}</span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <span className={`badge ${user.is_active ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '12px' }}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </span>
                          {(user.must_change_password === true || user.must_change_password === 1) && (
                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                              Password reset required
                            </span>
                          )}
                        </div>
                      </td>
                      <td>
                        <div>
                          <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                            {new Date(user.created_at).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                          <div style={{ fontSize: '12px', color: '#64748b' }}>
                            {new Date(user.created_at).toLocaleTimeString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleEdit(user)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => toggleUserStatus(user)}
                          >
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleDelete(user)}
                            style={{ borderColor: '#ef4444', color: '#ef4444' }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              pagination={pagination}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              loading={loading}
            />
          </>
        )}
      </div>

      {/* Add/Edit User Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingUser ? 'Edit User' : 'Add New User'}</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>×</button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input
                    type="text"
                    className="form-input"
                    value={formData.fullName}
                    onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                    placeholder="Enter full name"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input
                    type="email"
                    className="form-input"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email address"
                    required
                  />
                  <p style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                    This will also be used as the username for login
                  </p>
                </div>

                <div className="form-group">
                  <label className="form-label">Role *</label>
                  <select
                    className="form-input"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                  >
                    {roles.length === 0 ? (
                      <option value="">Loading roles...</option>
                    ) : (
                      roles.map((role) => {
                        // Add emoji based on role name for better UX
                        const getEmoji = (roleName) => {
                          switch (roleName) {
                            case 'admin': return <Icon name="crown" />;
                            case 'financial_officer': return <Icon name="briefcase" />;
                            case 'recruiter': return <Icon name="user" />;
                            default: return <Icon name="tool" />;
                          }
                        };

                        return (
                          <option key={role.id} value={role.name}>
                            {getEmoji(role.name)} {role.display_name || role.name}
                          </option>
                        );
                      })
                    )}
                  </select>
                </div>

                {!editingUser && (
                  <div style={{
                    background: '#f0f9ff',
                    border: '1px solid #bae6fd',
                    borderRadius: '8px',
                    padding: '16px',
                    marginTop: '16px'
                  }}>
                    <h4 style={{ color: '#0c4a6e', margin: '0 0 8px', fontSize: '14px', fontWeight: '600' }}>
                      <Icon name="mail" /> Email Notification
                    </h4>
                    <p style={{ color: '#0369a1', margin: 0, fontSize: '13px', lineHeight: '1.4' }}>
                      A temporary password will be generated and sent to the user's email address.
                      They will be required to change it on first login.
                    </p>
                  </div>
                )}
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingUser ? <><Icon name="save" />{" Update User"}</> : <><Icon name="user" />{" Create User"}</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}


function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('valid'); // 'valid' or 'invalid'
  const [searchTerm, setSearchTerm] = useState('');
  const [recruiterFilter, setRecruiterFilter] = useState('');
  const [recruitersList, setRecruitersList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, limit: 50, page: 1 });

  useEffect(() => {
    loadStudents();
  }, [currentPage, recruiterFilter, activeTab, searchTerm]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [recruiterFilter, activeTab, searchTerm]);

  const loadStudents = async () => {
    try {
      setLoading(true);
      const isInvalid = activeTab === 'invalid';
      const response = await studentAPI.getAllStudents(currentPage, pagination.limit, recruiterFilter, isInvalid, searchTerm);

      setStudents(response.data.students || []);
      setPagination(response.data.pagination || { total: 0, limit: 50, page: 1 });

      // Load recruiters list for filter if not already loaded
      if (recruitersList.length === 0) {
        const recruitersResponse = await recruiterAPI.getAll(1, 1000);
        setRecruitersList(recruitersResponse.data.recruiters || []);
      }
    } catch (error) {
      console.error('Failed to load students:', error);
      toast.error('Failed to load students data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStudent = async (studentId) => {
    if (window.confirm(`Are you sure you want to permanently delete student ${studentId}?`)) {
      try {
        await studentAPI.deleteStudent(studentId);
        toast.success(`Student ${studentId} deleted successfully`);
        loadStudents(); // Refresh data
      } catch (error) {
        console.error('Delete failed:', error);
        toast.error(error.message || 'Failed to delete student');
      }
    }
  };

  // Since primary filtering is now server-side, display list is the students array
  const displayStudents = students;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <h1 className="page-title">Students Overview</h1>
          <p className="page-subtitle">View and filter all admitted and enrolled students across the system</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={loadStudents}>
          <svg style={{ width: '16px', height: '16px', fill: 'none', stroke: 'currentColor', strokeWidth: 2, marginRight: '8px' }} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh Data
        </button>
      </div>

      {/* Tabs */}
      <div className="tabs" style={{ marginBottom: '20px', borderBottom: '1px solid #e2e8f0', display: 'flex' }}>
        <button
          className={`tab-btn ${activeTab === 'valid' ? 'active' : ''}`}
          onClick={() => { setActiveTab('valid'); setRecruiterFilter(''); setSearchTerm(''); }}
          style={{ padding: '12px 24px', background: 'none', border: 'none', borderBottom: activeTab === 'valid' ? '2px solid #037b90' : '2px solid transparent', color: activeTab === 'valid' ? '#037b90' : '#64748b', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
        >
          Students by Valid Recruiters
        </button>
        <button
          className={`tab-btn ${activeTab === 'invalid' ? 'active' : ''}`}
          onClick={() => { setActiveTab('invalid'); setRecruiterFilter(''); setSearchTerm(''); }}
          style={{ padding: '12px 24px', background: 'none', border: 'none', borderBottom: activeTab === 'invalid' ? '2px solid #ef4444' : '2px solid transparent', color: activeTab === 'invalid' ? '#ef4444' : '#64748b', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}
        >
          Invalid Code Students
        </button>
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 className="card-title">{activeTab === 'valid' ? 'All Valid Students' : 'Invalid Source Students'}</h2>
            <p className="card-subtitle">Showing {displayStudents.length} of {pagination?.total || 0} students</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            {activeTab === 'valid' && (
              <div className="form-group" style={{ marginBottom: 0, minWidth: '200px' }}>
                <select
                  className="form-control"
                  value={recruiterFilter}
                  onChange={(e) => setRecruiterFilter(e.target.value)}
                  style={{ padding: '8px 12px' }}
                >
                  <option value="">All Recruiters</option>
                  {recruitersList.map(r => (
                    <option key={r.id} value={r.id}>{r.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div className="navbar-search" style={{ minWidth: '250px' }}>
              <input
                type="text"
                className="navbar-search-input"
                style={{ width: '100%', borderRadius: '4px' }}
                placeholder="Search by ID, Name or Referral..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="table-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <div className="loading" style={{ width: '32px', height: '32px', margin: '0 auto 16px', border: '3px solid #f3f3f3', borderTop: '3px solid #037b90', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              <p>Loading students data...</p>
            </div>
          ) : displayStudents.length > 0 ? (
            <table className="table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Programme</th>
                  <th>Status</th>
                  <th>Recruiter</th>
                  <th>Date</th>
                  {activeTab === 'invalid' && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {displayStudents.map((student, index) => (
                  <tr key={`${student.student_id}-${index}`}>
                    <td style={{ fontWeight: '600' }}>{student.student_id}</td>
                    <td>{student.student_name || "—"}</td>
                    <td><span style={{ fontSize: '12px', color: '#64748b' }}>{student.programme}</span></td>
                    <td>
                      <span className={`badge badge-${student.status === 'Enrolled' ? 'success' : 'info'}`}>
                        {student.status}
                      </span>
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: '500', fontSize: '13px', color: student.recruiter_id === 1 ? '#ef4444' : 'inherit' }}>{student.recruiter_name}</div>
                        <div style={{ fontSize: '11px', color: '#94a3b8' }}>Code: {student.referral_code}</div>
                      </div>
                    </td>
                    <td style={{ fontSize: '13px' }}>
                      {student.date ? new Date(student.date).toLocaleDateString() : '—'}
                    </td>
                    {activeTab === 'invalid' && (
                      <td>
                        <button
                          onClick={() => handleDeleteStudent(student.student_id)}
                          className="btn btn-outline btn-sm"
                          style={{ borderColor: '#ef4444', color: '#ef4444' }}
                          title="Permanently remove student record from system"
                        >
                          Delete
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#64748b' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', opacity: 0.5 }}><Icon name="graduation-cap" /></div>
              <h3 style={{ fontWeight: '600', marginBottom: '8px' }}>No Students Found</h3>
              <p style={{ fontSize: '14px' }}>Try adjusting your filters or search terms.</p>
            </div>
          )}
        </div>
        <Pagination
          pagination={pagination}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          loading={loading}
        />
      </div>
    </div>
  );
}

function AdminRecruiterDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [recruiter, setRecruiter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('students');
  const [showQRModal, setShowQRModal] = useState(false);

  useEffect(() => {
    loadRecruiter();
  }, [id]);

  const loadRecruiter = async () => {
    try {
      const response = await recruiterAPI.get(id);
      setRecruiter(response.data || response);
    } catch (error) {
      toast.error(error.message || 'Failed to load recruiter');
      console.error('Load recruiter error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Loading recruiter details...</p>
        </div>
      </div>
    );
  }

  if (!recruiter) {
    return (
      <div className="main-content">
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <p>Recruiter not found</p>
          <Link to="/admin/recruiters" className="btn btn-primary">Back to Recruiters</Link>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'students':
        return <AdminStudentsTab students={recruiter.students || []} />;
      case 'admissions':
        return <AdminAdmissionsTab admissions={recruiter.admissions || []} />;
      case 'enrollments':
        return <AdminEnrollmentsTab enrollments={recruiter.enrollments || []} />;
      case 'payments':
        return <AdminPaymentsTab payments={recruiter.payments || []} />;
      default:
        return null;
    }
  };

  return (
    <div className="main-content">
      <div className="dashboard-header">
        <div>
          <h1>{recruiter.full_name}</h1>
          <p style={{ color: '#64748b', margin: 0 }}>
            Referral Code: {recruiter.referral_code} | 
            Email: {recruiter.email}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-sm btn-outline" onClick={() => setShowQRModal(true)}>
            QR Verify
          </button>
          <button className="btn btn-secondary" onClick={() => navigate('/admin/recruiters')}>
            Back to Recruiters
          </button>
        </div>
      </div>

      <div className="dashboard-grid" style={{ marginBottom: '24px' }}>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Admissions</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>{recruiter.total_admissions || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Enrollments</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>{recruiter.total_enrollments || 0}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Total Earnings</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>KES {Number(recruiter.total_earned || 0).toFixed(2)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body" style={{ textAlign: 'center' }}>
            <h3 style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Payment Status</h3>
            <p className="stat-value" style={{ margin: '8px 0 0' }}>
              <span className={`status-badge ${recruiter.payment_status || 'pending'}`}>
                {recruiter.payment_status || 'pending'}
              </span>
            </p>
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '24px' }}>
        <div className="card-header">
          <h3>Personal Information</h3>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Full Name</label>
              <p style={{ margin: '4px 0' }}>{recruiter.full_name}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Mobile Number</label>
              <p style={{ margin: '4px 0' }}>{recruiter.mobile_number}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Nationality</label>
              <p style={{ margin: '4px 0' }}>{recruiter.nationality}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>ID/Passport</label>
              <p style={{ margin: '4px 0' }}>{recruiter.id_passport}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>KRA PIN</label>
              <p style={{ margin: '4px 0' }}>{recruiter.kra_pin || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Status</label>
              <p style={{ margin: '4px 0' }}>
                <span className={`status-badge ${recruiter.status}`}>{recruiter.status}</span>
              </p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>County</label>
              <p style={{ margin: '4px 0' }}>{recruiter.county}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Subcounty</label>
              <p style={{ margin: '4px 0' }}>{recruiter.subcounty || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Residential Address</label>
              <p style={{ margin: '4px 0' }}>{recruiter.residential_address}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Bank Account</label>
              <p style={{ margin: '4px 0' }}>{recruiter.bank_account || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Bank Name</label>
              <p style={{ margin: '4px 0' }}>{recruiter.bank_name || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Branch Code</label>
              <p style={{ margin: '4px 0' }}>{recruiter.branch_code || 'Not provided'}</p>
            </div>
            <div>
              <label style={{ fontWeight: 600, color: '#64748b', fontSize: '13px' }}>Referral Code</label>
              <p style={{ margin: '4px 0' }}>{recruiter.referral_code}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header" style={{ borderBottom: 'none', paddingBottom: 0 }}>
          <div className="tabs" style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #e2e8f0', width: '100%' }}>
            {[
              { id: 'students', label: 'Students', count: (recruiter.students || []).length },
              { id: 'admissions', label: 'Admissions', count: (recruiter.admissions || []).length },
              { id: 'enrollments', label: 'Enrollments', count: (recruiter.enrollments || []).length },
              { id: 'payments', label: 'Payments', count: (recruiter.payments || []).length }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontWeight: activeTab === tab.id ? 600 : 400,
                  color: activeTab === tab.id ? '#037b90' : '#64748b',
                  borderBottom: activeTab === tab.id ? '2px solid #037b90' : '2px solid transparent',
                  marginBottom: '-2px',
                  fontSize: '14px'
                }}
              >
                {tab.label} ({tab.count})
              </button>
            ))}
          </div>
        </div>
        <div className="card-body" style={{ paddingTop: '20px' }}>
          {renderTabContent()}
        </div>
      </div>

      {showQRModal && (
        <div className="modal-overlay" onClick={() => setShowQRModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Recruiter Verification QR</h3>
              <button className="modal-close" onClick={() => setShowQRModal(false)}>×</button>
            </div>
            <div className="modal-body" style={{ textAlign: 'center', padding: '32px' }}>
              <QRCodeSVG value={`${window.location.origin}/verify?code=${encodeURIComponent(recruiter.referral_code)}`} size={200} />
              <p style={{ margin: '16px 0 4px', fontWeight: 600, fontSize: '16px', color: '#1e293b' }}>{recruiter.full_name}</p>
              <p style={{ margin: 0, color: '#64748b', fontSize: '14px' }}>Code: {recruiter.referral_code}</p>
              <div style={{ marginTop: '16px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
                <p style={{ margin: 0, fontSize: '13px', color: '#166534' }}>
                  Share this QR code with students to verify you are a genuine OUK recruiter.
                </p>
              </div>
              <button
                className="btn btn-primary"
                style={{ marginTop: '16px' }}
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/verify?code=${encodeURIComponent(recruiter.referral_code)}`);
                  toast.success('Verification link copied!');
                }}
              >
                Copy Verification Link
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminStudentsTab({ students }) {
  if (students.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No students recruited yet</p>;
  }

  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Course</th>
          <th>Admission Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {students.map((student) => (
          <tr key={student.id}>
            <td>{student.student_name}</td>
            <td>{student.student_id}</td>
            <td>{student.course || 'N/A'}</td>
            <td>{student.admission_date ? new Date(student.admission_date).toLocaleDateString() : 'N/A'}</td>
            <td>
              <span className={`status-badge ${student.admission_verified ? 'active' : 'pending'}`}>
                {student.admission_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(student.commission_amount || 0).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function AdminAdmissionsTab({ admissions }) {
  if (admissions.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No admissions recorded</p>;
  }

  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Admission Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {admissions.map((admission) => (
          <tr key={admission.id}>
            <td>{admission.student_name || 'N/A'}</td>
            <td>{admission.student_id}</td>
            <td>{new Date(admission.admission_date).toLocaleDateString()}</td>
            <td>
              <span className={`status-badge ${admission.admission_verified ? 'active' : 'pending'}`}>
                {admission.admission_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(admission.commission_amount || 500).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function AdminEnrollmentsTab({ enrollments }) {
  if (enrollments.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No enrollments recorded</p>;
  }

  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Student Name</th>
          <th>Student ID</th>
          <th>Enrollment Date</th>
          <th>Verified</th>
          <th>Commission</th>
        </tr>
      </thead>
      <tbody>
        {enrollments.map((enrollment) => (
          <tr key={enrollment.id}>
            <td>{enrollment.student_name || 'N/A'}</td>
            <td>{enrollment.student_id}</td>
            <td>{new Date(enrollment.enrollment_date).toLocaleDateString()}</td>
            <td>
              <span className={`status-badge ${enrollment.enrollment_verified ? 'active' : 'pending'}`}>
                {enrollment.enrollment_verified ? 'Verified' : 'Pending'}
              </span>
            </td>
            <td>KES {Number(enrollment.commission_amount || 300).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

function AdminPaymentsTab({ payments }) {
  if (payments.length === 0) {
    return <p style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>No payment records yet</p>;
  }

  return (
    <div className="table-container">
    <table className="table">
      <thead>
        <tr>
          <th>Date</th>
          <th>Admissions</th>
          <th>Enrollments</th>
          <th>Total Amount</th>
          <th>Status</th>
          <th>Reference</th>
        </tr>
      </thead>
      <tbody>
        {payments.map((payment) => (
          <tr key={payment.id}>
            <td>{payment.created_at ? new Date(payment.created_at).toLocaleDateString() : 'N/A'}</td>
            <td>{payment.total_admissions || 0}</td>
            <td>{payment.total_enrollments || 0}</td>
            <td>KES {Number(payment.cumulative_amount || 0).toFixed(2)}</td>
            <td>
              <span className={`status-badge ${payment.payment_status || 'pending'}`}>
                {payment.payment_status || 'pending'}
              </span>
            </td>
            <td>{payment.payment_reference || 'N/A'}</td>
          </tr>
        ))}
      </tbody>
    </table>
    </div>
  );
}

export default AdminDashboard;
